import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Link2, RefreshCcw, Upload, Database, Wand2, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import { adminReportingAPI } from '../api';
import {
  ACTIVE_AUTOMATION_PROFILE_CONTEXT_KEY,
  campaignFieldOptions,
  LINKTRUST_SESSION_CREDENTIALS_KEY,
  parseCreativeImportFile,
  parseImportFile,
  remapRowsByFieldOverrides,
  creativeFieldOptions,
  suggestCampaignFieldByHeader,
  suggestCreativeFieldByHeader,
  suggestCreativeTypeFromFile,
  summarizeCreativeMappedRow,
  summarizeMappedRow
} from '../utils/linktrustImport';

const workflowModes = [
  {
    id: 'platform',
    label: 'Automation Jobs',
    description: 'Data sync and import automation only.'
  }
];

const workflows = [
  { id: 'sync', label: 'Data Sync', mode: 'platform' },
  { id: 'campaigns', label: 'Campaign Import', mode: 'platform' },
  { id: 'creatives', label: 'Creative Import', mode: 'platform' },
  { id: 'links', label: 'Link Builder', mode: 'platform' }
];

const initialCredentials = {
  username: '',
  password: '',
  merchantId: '',
  apiUrl: 'https://api.linktrust.com/v2/Campaigns/Campaign',
  transactionApiUrl: 'https://api.linktrust.com/v2/Transaction/TransactionByCampaignId',
  clickListingApiUrl: 'https://api.linktrust.com/v2/reports/clicklisting'
};

const LOCKED_CLICK_LISTING_API_URL = 'https://api.linktrust.com/v2/reports/clicklisting';

const toDisplayError = (error, fallbackMessage) => {
  const detailRows = Array.isArray(error?.details) ? error.details : [];
  if (!detailRows.length) {
    return error?.message || fallbackMessage;
  }

  const firstDetail = detailRows[0];
  if (typeof firstDetail === 'string') {
    return `${error.message || fallbackMessage}: ${firstDetail}`;
  }
  const detailMessage = firstDetail?.msg || firstDetail?.message || JSON.stringify(firstDetail);
  return `${error.message || fallbackMessage}: ${detailMessage}`;
};

const normalizeMerchantId = (value) => String(value || '').replace(/[^0-9]/g, '');
const parseNumericIdList = (value) => Array.from(new Set(
  String(value || '')
    .split(/[\s,;|]+/)
    .map((item) => Number.parseInt(item, 10))
    .filter((item) => Number.isFinite(item) && item > 0)
));

const normalizeSheetValue = (value) => {
  if (value == null) return '';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return value;
};

const downloadWorkbook = (filename, rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const normalizedRows = list.map((row, index) => {
    if (row && typeof row === 'object' && !Array.isArray(row)) {
      const normalized = {};
      Object.entries(row).forEach(([key, value]) => {
        normalized[key] = normalizeSheetValue(value);
      });
      return normalized;
    }
    return { value: normalizeSheetValue(row), rowIndex: index };
  });

  const worksheet = XLSX.utils.json_to_sheet(normalizedRows.length ? normalizedRows : [{ info: 'No transactions returned' }]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');
  XLSX.writeFile(workbook, filename);
};

const normalizeExportValue = (value, { preferStringValue = false } = {}) => {
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? 'True' : 'False';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'string') return value;

  if (Array.isArray(value)) {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  if (typeof value === 'object') {
    const stringValue = value.StringValue ?? value.stringValue;
    if (preferStringValue && stringValue != null && typeof stringValue !== 'object') {
      return String(stringValue);
    }

    if (stringValue != null && typeof stringValue !== 'object') {
      return String(stringValue);
    }

    const primitiveKeys = ['value', 'Value', 'id', 'Id', 'ID'];
    for (const key of primitiveKeys) {
      const candidate = value[key];
      if (candidate != null && typeof candidate !== 'object') {
        return String(candidate);
      }
    }

    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
};

const pickFirstValue = (row, keys = [], options = {}) => {
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(row || {}, key)) continue;
    const value = normalizeExportValue(row?.[key], options);
    if (value !== '') {
      return value;
    }
  }
  return '';
};

const normalizeBooleanText = (value) => {
  if (typeof value === 'boolean') return value ? 'True' : 'False';
  const text = String(value || '').trim().toLowerCase();
  if (!text) return '';
  if (['true', '1', 'yes'].includes(text)) return 'True';
  if (['false', '0', 'no'].includes(text)) return 'False';
  return value;
};

const formatLt7TransactionDetailsRows = (rows = [], { includeRawRemainder = false } = {}) => {
  const sourceRows = Array.isArray(rows) ? rows : [];
  const formattedRows = sourceRows.map((row) => ({
    'Transaction Id': pickFirstValue(row, ['TransactionId', 'StringValue', 'TransactionID', 'TransactionId', 'TID', 'Id'], { preferStringValue: true }),
    'Conversion Date Time': pickFirstValue(row, ['ConversionDateTimeString', 'ConversionDateTimeUtc', 'Date', 'ConversionDate', 'ConversionDateTime', 'PartnerLocalDate']),
    CID: pickFirstValue(row, ['CampaignId', 'CID', 'CampaignID', '__campaign_id']),
    ADID: pickFirstValue(row, ['CreativeId', 'ADID', 'AdId', 'AdID']),
    AffiliateId: pickFirstValue(row, ['AffiliateId', 'AffiliateID']),
    SID: pickFirstValue(row, ['SubId', 'CampaignStepId', 'SID', 'StepId', 'StepID']),
    Commission: pickFirstValue(row, ['TotalCustomAffiliateCommission', 'AffiliatePayoutAmount', 'Commission']),
    Revenue: pickFirstValue(row, ['TotalCustomPartnerRevenue', 'MerchantPayoutAmount', 'Revenue', 'AdvertiserRevenue']),
    IsApproved: normalizeBooleanText(pickFirstValue(row, ['IsApproved', 'Approval'])),
    'Approval Reason': pickFirstValue(row, ['ApprovalReason', 'ApprovalStatusReason']),
    Modified: normalizeBooleanText(pickFirstValue(row, ['HasBeenModified', 'Modified'])),
    Quantity: pickFirstValue(row, ['Quantity']),
    Currency: pickFirstValue(row, ['CurrencyCode', 'Currency']),
    'Total Sales': pickFirstValue(row, ['TotalSales', 'TotalSaleAmount', 'SaleAmount']),
    Payout: pickFirstValue(row, ['AffiliatePayoutAmount', 'Payout']),
    'Payout Type': pickFirstValue(row, ['PayoutType']),
    'Payout Reason': pickFirstValue(row, ['PayoutReason']),
    'Unit Revenue': pickFirstValue(row, ['UnitRevenue']),
    'Revenue Type': pickFirstValue(row, ['RevenueType']),
    'Click Date Time': pickFirstValue(row, ['ClickDateTimeString', 'ClickDateTimeUtc', 'ClickDate', 'ClickDateTime']),
    'Click Id': pickFirstValue(row, ['ClickID', 'ClickId']),
    'Click IP Address': pickFirstValue(row, ['ClickIPAddressString', 'ClickIPAddress', 'ClickIpAddress']),
    'Click IP Country Code': pickFirstValue(row, ['ClickCountry', 'ClickCountryCode']),
    'Conversion Ip Address': pickFirstValue(row, ['ConversionIPAddressString', 'ConversionIPAddress', 'ConversionIpAddress']),
    ConversionIPCountryCode: pickFirstValue(row, ['ConversionCountry', 'ConversionCountryCode']),
    'Click Referrer': pickFirstValue(row, ['ClickReferrer']),
    'Conversion Referrer': pickFirstValue(row, ['ConversionReferrer']),
    'Conversion Type': pickFirstValue(row, ['ConversionMethodTypeIdString', 'ConversionType']),
    'Advertiser Reference Id': pickFirstValue(row, ['MerchantReferenceId', 'AdvertiserRefID', 'AdvertiserReferenceId']),
    'Affiliate Reference Id': pickFirstValue(row, ['AffiliateRefID', 'AffiliateReferenceId']),
    'Click Additional Data': pickFirstValue(row, ['AdditionalClickData', 'ClickAdditionalData']),
    'Conversion Additional Data': pickFirstValue(row, ['AdditionalConversionData', 'ConversionAdditionalData', 'AdditionalData']),
    'Click User Agent': pickFirstValue(row, ['ClickUserAgent']),
    'Conversion User Agent': pickFirstValue(row, ['ConversionUserAgent']),
    RevenueReason: pickFirstValue(row, ['RevenueReason'])
  }));

  if (!includeRawRemainder) {
    return formattedRows;
  }

  const knownHeaders = new Set(LT7_TRANSACTION_DETAIL_COLUMNS);
  const rawKeyOrder = [];
  const rawKeySet = new Set();
  sourceRows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      const header = `Raw:${key}`;
      if (knownHeaders.has(key) || rawKeySet.has(header)) return;
      rawKeySet.add(header);
      rawKeyOrder.push(header);
    });
  });

  return formattedRows.map((row, index) => {
    const source = sourceRows[index] || {};
    const withRaw = { ...row };
    rawKeyOrder.forEach((rawHeader) => {
      const sourceKey = rawHeader.slice(4);
      withRaw[rawHeader] = normalizeExportValue(source[sourceKey]);
    });
    return withRaw;
  });
};

const buildLt7RawTailColumns = (rows = []) => {
  const rawKeyOrder = [];
  const rawKeySet = new Set();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      const header = `Raw:${key}`;
      if (rawKeySet.has(header)) return;
      rawKeySet.add(header);
      rawKeyOrder.push(header);
    });
  });
  return [...LT7_TRANSACTION_DETAIL_COLUMNS, ...rawKeyOrder];
};

const LT7_TRANSACTION_DETAIL_COLUMNS = [
  'Transaction Id',
  'Conversion Date Time',
  'CID',
  'ADID',
  'AffiliateId',
  'SID',
  'Commission',
  'Revenue',
  'IsApproved',
  'Approval Reason',
  'Modified',
  'Quantity',
  'Currency',
  'Total Sales',
  'Payout',
  'Payout Type',
  'Payout Reason',
  'Unit Revenue',
  'Revenue Type',
  'Click Date Time',
  'Click Id',
  'Click IP Address',
  'Click IP Country Code',
  'Conversion Ip Address',
  'ConversionIPCountryCode',
  'Click Referrer',
  'Conversion Referrer',
  'Conversion Type',
  'Advertiser Reference Id',
  'Affiliate Reference Id',
  'Click Additional Data',
  'Conversion Additional Data',
  'Click User Agent',
  'Conversion User Agent',
  'RevenueReason'
];

const LT7_CLICK_SUMMARY_COLUMNS = [
  'date',
  'afid',
  'total_clicks',
  'valid_clicks',
  'filtered_clicks',
  'duplicate_clicks',
  'unknown_status_clicks'
];

const LT7_CLICK_RAW_COLUMNS = [
  'Date/Time',
  'Click ID',
  'Status',
  'Filter Reason',
  'CID',
  'AFID',
  'ADID',
  'SID',
  'Affiliate Reference ID',
  'Country',
  'IP Address',
  'User Agent',
  'Referrer'
];

const formatClickListingRawRows = (rows = []) => (Array.isArray(rows) ? rows : []).map((row) => ({
  'Date/Time': pickFirstValue(row, ['ClickDateTime', 'Date', 'DateTime', 'clickDateTime']),
  'Click ID': pickFirstValue(row, ['ClickId', 'ClickID', 'Id', 'ID']),
  Status: pickFirstValue(row, ['Status', 'ClickStatus', 'LeadStatus', 'TrafficFilterStatus']),
  'Filter Reason': pickFirstValue(row, ['FilterReason', 'StatusReason', 'Reason', 'TrafficFilterReason']),
  CID: pickFirstValue(row, ['CampaignId', 'CID', '__campaign_id']),
  AFID: pickFirstValue(row, ['AffiliateId', 'AFID']),
  ADID: pickFirstValue(row, ['CreativeId', 'ADID', 'AdId']),
  SID: pickFirstValue(row, ['SubId', 'SID', 'StepId']),
  'Affiliate Reference ID': pickFirstValue(row, ['AffiliateReferenceId', 'AffiliateRefID']),
  Country: pickFirstValue(row, ['CountryCode', 'Country']),
  'IP Address': pickFirstValue(row, ['IPAddress', 'IpAddress', 'IP']),
  'User Agent': pickFirstValue(row, ['UserAgent', 'ClickUserAgent']),
  Referrer: pickFirstValue(row, ['Referrer', 'ClickReferrer'])
}));

const summarizeTransactionRows = (rows = []) => {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const campaignId = pickFirstValue(row, ['CampaignId', 'CID', '__campaign_id']) || 'unknown';
    const affiliateId = pickFirstValue(row, ['AffiliateId', 'AffiliateID']) || 'unknown';
    const approvalRaw = normalizeBooleanText(pickFirstValue(row, ['IsApproved', 'Approval']));
    const status = approvalRaw === 'True' ? 'Approved' : approvalRaw === 'False' ? 'Rejected' : 'Unknown';
    const revenue = Number.parseFloat(pickFirstValue(row, ['TotalCustomPartnerRevenue', 'MerchantPayoutAmount', 'Revenue', 'AdvertiserRevenue']) || '0') || 0;
    const commission = Number.parseFloat(pickFirstValue(row, ['TotalCustomAffiliateCommission', 'AffiliatePayoutAmount', 'Commission']) || '0') || 0;
    const key = `${campaignId}__${affiliateId}__${status}`;
    const current = map.get(key) || {
      CID: campaignId,
      AffiliateId: affiliateId,
      Status: status,
      Transactions: 0,
      Revenue: 0,
      Commission: 0
    };
    current.Transactions += 1;
    current.Revenue += revenue;
    current.Commission += commission;
    map.set(key, current);
  });

  return Array.from(map.values()).map((row) => ({
    ...row,
    Revenue: Number(row.Revenue.toFixed(4)),
    Commission: Number(row.Commission.toFixed(4))
  }));
};

const escapeCsvCell = (value) => {
  const text = normalizeExportValue(value, { preferStringValue: true });
  const escaped = text.replace(/"/g, '""');
  return `"${escaped}"`;
};

const downloadCsv = (filename, rows, columns) => {
  const header = columns.map(escapeCsvCell).join(',');
  const body = (Array.isArray(rows) ? rows : []).map((row) => columns.map((col) => escapeCsvCell(row?.[col] ?? '')).join(',')).join('\n');
  const csvContent = `${header}\n${body}`;
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const buildScannedCreativeCandidatesFromAssets = ({ assets, campaignOptions }) => {
  const campaignIdSet = new Set(campaignOptions.map((campaign) => String(campaign.linktrust_campaign_id || '')).filter(Boolean));
  const cleanLabel = (value) => String(value || '')
    .replace(/\.[^.]+$/, '')
    .replace(/^ads?[_-]?/i, '')
    .replace(/^\d{5,10}[_\s-]+/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const extractLeadingAdMeta = (value) => {
    const text = String(value || '').replace(/\.[^.]+$/, '').trim();
    const match = text.match(/^(?:ads?[_-]?)?(\d{6,10})[_\s-]+(.+)$/i);
    if (!match) return null;
    return {
      adId: match[1],
      adName: cleanLabel(match[2])
    };
  };

  const genericCreativeNames = new Set(['error', 'index', 'readme', 'thumbs', 'desktop']);
  const noisyExtensions = new Set(['txt', 'log', 'md', 'json', 'xml']);
  const htmlExtensions = new Set(['html', 'htm']);
  const imageAssetFolderNames = new Set(['images', 'image', 'img', 'assets', 'asset']);

  const rootKeyByAsset = (assets || []).map((asset, index) => {
    const localPath = asset.localPath || asset.originalName || `asset-${index + 1}`;
    const normalizedPath = String(localPath || '').replace(/\\/g, '/');
    const pathParts = normalizedPath.split('/').filter(Boolean);
    const adFolderIndex = pathParts.findIndex((part) => /^(?:ads?[_-]?)?\d{6,10}[_\s-]+.+$/i.test(String(part)));
    if (adFolderIndex >= 0) {
      return pathParts.slice(0, adFolderIndex + 1).join('/');
    }
    return pathParts.slice(0, Math.max(pathParts.length - 1, 1)).join('/');
  });

  const rootsWithHtml = new Set();
  (assets || []).forEach((asset, index) => {
    const localPath = asset.localPath || asset.originalName || `asset-${index + 1}`;
    const extension = String(localPath).split('.').pop()?.toLowerCase() || '';
    if (htmlExtensions.has(extension)) {
      rootsWithHtml.add(rootKeyByAsset[index]);
    }
  });

  return assets.map((asset, index) => {
    const localPath = asset.localPath || asset.originalName || `asset-${index + 1}`;
    const normalizedPath = String(localPath || '').replace(/\\/g, '/');
    const pathParts = normalizedPath.split('/').filter(Boolean);
    const parentFolder = pathParts.length > 1 ? pathParts[pathParts.length - 2] : '';
    const baseName = String(normalizedPath).split('/').pop() || asset.originalName || `creative-${index + 1}`;
    const extension = String(baseName).split('.').pop()?.toLowerCase() || '';
    const prettyName = cleanLabel(baseName);
    const typeSuggestion = suggestCreativeTypeFromFile({ name: baseName, type: asset.mimeType || '' });
    const folderHint = cleanLabel(parentFolder);
    const rootKey = rootKeyByAsset[index];

    const containsAssetFolder = pathParts.some((part) => imageAssetFolderNames.has(String(part || '').toLowerCase()));
    const hasHtmlForRoot = rootsWithHtml.has(rootKey);
    const shouldSkipBecauseNestedAsset = hasHtmlForRoot && !htmlExtensions.has(extension) && containsAssetFolder;
    const shouldSkipBecauseNoisy = noisyExtensions.has(extension);
    if (shouldSkipBecauseNestedAsset || shouldSkipBecauseNoisy) {
      return null;
    }

    const numberCandidates = Array.from(new Set(
      pathParts
        .flatMap((part) => (String(part).match(/\b\d{5,10}\b/g) || []).map((item) => String(item)))
    ));
    const campaignIdFromPath = numberCandidates.find((candidate) => campaignIdSet.has(candidate)) || '';

    const trailingParts = pathParts.slice(-3).reverse();
    const adMetaFromPath = trailingParts
      .map((part) => extractLeadingAdMeta(part))
      .find(Boolean)
      || extractLeadingAdMeta(baseName);
    const adIdFromPath = adMetaFromPath?.adId || '';

    const campaignMatch = campaignOptions.find((campaign) => {
      if (campaignIdFromPath && String(campaign.linktrust_campaign_id) === campaignIdFromPath) {
        return true;
      }
      const candidate = String(campaign.campaign_name || '').toLowerCase();
      if (!candidate) return false;
      const hint = cleanLabel(pathParts[0] || folderHint).toLowerCase();
      return hint && (candidate.includes(hint) || hint.includes(candidate));
    });

    const campaignId = campaignMatch
      ? String(campaignMatch.linktrust_campaign_id)
      : campaignIdFromPath;

    const campaignNameByMatch = campaignMatch
      ? String(campaignMatch.campaign_name || '')
      : cleanLabel(pathParts[0] || folderHint || '');

    const fallbackNameFromFolder = cleanLabel(parentFolder);
    const isGenericBaseName = genericCreativeNames.has(prettyName.toLowerCase());
    const creativeNameByAd = adMetaFromPath?.adName
      || (isGenericBaseName && fallbackNameFromFolder ? fallbackNameFromFolder : prettyName)
      || `Creative ${index + 1}`;

    return {
      detectedCampaignId: campaignId,
      detectedCampaignName: campaignNameByMatch,
      detectedAdId: adIdFromPath,
      detectedCreativeName: creativeNameByAd,
      fallbackCampaignName: cleanLabel(pathParts[0] || folderHint || ''),
      fallbackCreativeName: prettyName || `Creative ${index + 1}`,
      fileName: localPath,
      creativeType: typeSuggestion.label,
      fileOrImageUrl: asset.url
    };
  }).filter(Boolean);
};

const mapScannedCreativeCandidates = ({ candidates, scannerMapper }) => {
  const pick = (row, key) => String(row?.[key] || '').trim();
  return (candidates || []).map((row, index) => ({
    name: pick(row, scannerMapper.creativeName) || `Creative ${index + 1}`,
    campaignName: pick(row, scannerMapper.campaignName),
    campaignId: pick(row, scannerMapper.campaignId).replace(/[^0-9]/g, ''),
    adId: pick(row, scannerMapper.adId).replace(/[^0-9]/g, ''),
    fileName: row.fileName,
    creativeType: row.creativeType,
    fileOrImageUrl: row.fileOrImageUrl,
    detectedCampaignId: row.detectedCampaignId,
    detectedCampaignName: row.detectedCampaignName,
    detectedAdId: row.detectedAdId,
    detectedCreativeName: row.detectedCreativeName
  }));
};

const Automations = () => {
  const [activeMode, setActiveMode] = useState('platform');
  const [activeWorkflow, setActiveWorkflow] = useState('sync');
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const [credentials, setCredentials] = useState(initialCredentials);
  const [merchantOptions, setMerchantOptions] = useState([]);

  const [campaignRows, setCampaignRows] = useState([]);
  const [campaignHeaders, setCampaignHeaders] = useState([]);
  const [campaignFieldOverrides, setCampaignFieldOverrides] = useState({});
  const [campaignProfileName, setCampaignProfileName] = useState('');
  const [campaignProfiles, setCampaignProfiles] = useState([]);
  const [selectedCampaignProfileId, setSelectedCampaignProfileId] = useState('');
  const [campaignMappedRows, setCampaignMappedRows] = useState([]);
  const [campaignHeaderRowIndex, setCampaignHeaderRowIndex] = useState(0);
  const [campaignFileName, setCampaignFileName] = useState('');
  const [campaignDryRun, setCampaignDryRun] = useState(false);
  const [campaignStopOnError, setCampaignStopOnError] = useState(false);
  const [campaignCategoryOverride, setCampaignCategoryOverride] = useState('');
  const [campaignImporting, setCampaignImporting] = useState(false);
  const [campaignResult, setCampaignResult] = useState(null);

  const [creativeRows, setCreativeRows] = useState([]);
  const [creativeHeaders, setCreativeHeaders] = useState([]);
  const [creativeFieldOverrides, setCreativeFieldOverrides] = useState({});
  const [creativeProfileName, setCreativeProfileName] = useState('');
  const [creativeProfiles, setCreativeProfiles] = useState([]);
  const [selectedCreativeProfileId, setSelectedCreativeProfileId] = useState('');
  const [creativeMappedRows, setCreativeMappedRows] = useState([]);
  const [creativeHeaderRowIndex, setCreativeHeaderRowIndex] = useState(0);
  const [creativeFileName, setCreativeFileName] = useState('');
  const [creativeDryRun, setCreativeDryRun] = useState(false);
  const [creativeLocalOnly, setCreativeLocalOnly] = useState(false);
  const [creativeImporting, setCreativeImporting] = useState(false);
  const [creativeResult, setCreativeResult] = useState(null);
  const [uploadedAssets, setUploadedAssets] = useState([]);
  const [uploadingAssets, setUploadingAssets] = useState(false);
  const [scannedCreativeCandidates, setScannedCreativeCandidates] = useState([]);
  const [scannedCreativeRows, setScannedCreativeRows] = useState([]);
  const [scannerMapper, setScannerMapper] = useState({
    campaignId: 'detectedCampaignId',
    campaignName: 'detectedCampaignName',
    adId: 'detectedAdId',
    creativeName: 'detectedCreativeName'
  });

  const [syncingAffiliates, setSyncingAffiliates] = useState(false);
  const [syncingAdvertisers, setSyncingAdvertisers] = useState(false);
  const [syncingCampaigns, setSyncingCampaigns] = useState(false);
  const [savingCampaignPreview, setSavingCampaignPreview] = useState(false);
  const [syncingCreatives, setSyncingCreatives] = useState(false);
  const [syncingLeadStatus, setSyncingLeadStatus] = useState(false);
  const [syncingCampaignTransactions, setSyncingCampaignTransactions] = useState(false);
  const [syncingClickListing, setSyncingClickListing] = useState(false);
  const [advertiserSyncResult, setAdvertiserSyncResult] = useState(null);
  const [affiliateSyncResult, setAffiliateSyncResult] = useState(null);
  const [campaignSyncResult, setCampaignSyncResult] = useState(null);
  const [campaignSyncPreview, setCampaignSyncPreview] = useState(null);
  const [creativeSyncResult, setCreativeSyncResult] = useState(null);
  const [automationProfiles, setAutomationProfiles] = useState([]);
  const [selectedAutomationProfileId, setSelectedAutomationProfileId] = useState('');
  const [appliedAutomationProfile, setAppliedAutomationProfile] = useState(null);
  const [automationClientName, setAutomationClientName] = useState('');
  const [automationProfileName, setAutomationProfileName] = useState('');
  const [savingAutomationProfile, setSavingAutomationProfile] = useState(false);
  const [leadSyncResult, setLeadSyncResult] = useState(null);
  const [leadParams, setLeadParams] = useState({ startDate: '', endDate: '', trafficFilterLeadStats: '1' });
  const [campaignTransactionResult, setCampaignTransactionResult] = useState(null);
  const [campaignTransactionFormat, setCampaignTransactionFormat] = useState('raw');
  const [syncSectionTab, setSyncSectionTab] = useState('directory');
  const [automationActivityLog, setAutomationActivityLog] = useState([]);
  const [clickListingResult, setClickListingResult] = useState(null);
  const [clickListingJobRunId, setClickListingJobRunId] = useState(null);
  const [clickListingJobRun, setClickListingJobRun] = useState(null);
  const [clickListingJobs, setClickListingJobs] = useState([]);
  const [clickListingFormat, setClickListingFormat] = useState('both_csv');
  const [clickListingParams, setClickListingParams] = useState({
    startDate: '',
    endDate: '',
    campaignIdsText: '',
    useStoredCampaigns: false,
    affiliateId: '',
    autoResumeEnabled: true,
    autoResumeStallThresholdSeconds: '480'
  });
  const [campaignTransactionParams, setCampaignTransactionParams] = useState({
    startDate: '',
    endDate: '',
    campaignIdsText: '',
    useStoredCampaigns: true,
    affiliateId: '',
    adId: '',
    stepId: '',
    isApproved: false,
    strictThrottle: true,
    chunkSize: '35',
    interChunkDelayMs: '1200',
    promoCode: '',
    additionalIdType: '',
    additionalIds: ''
  });
  const [campaignTransactionChunkProgress, setCampaignTransactionChunkProgress] = useState(null);

  const [storedCampaigns, setStoredCampaigns] = useState([]);
  const [storedAdvertisers, setStoredAdvertisers] = useState([]);
  const [storedAffiliates, setStoredAffiliates] = useState([]);
  const [storedCreatives, setStoredCreatives] = useState([]);
  const [storedLinks, setStoredLinks] = useState([]);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
  const [selectedAffiliateIds, setSelectedAffiliateIds] = useState([]);
  const [selectedAdIds, setSelectedAdIds] = useState([]);
  const [bulkCampaignSearch, setBulkCampaignSearch] = useState('');
  const [bulkAdvertiserFilter, setBulkAdvertiserFilter] = useState('');
  const [bulkCategoryFilter, setBulkCategoryFilter] = useState('');
  const [campaignAdSelections, setCampaignAdSelections] = useState({});
  const [campaignAdDrafts, setCampaignAdDrafts] = useState({});
  const [creativeCampaignOverride, setCreativeCampaignOverride] = useState('');
  const [mappingAdvertiserScope, setMappingAdvertiserScope] = useState('');
  const [includeAdIds, setIncludeAdIds] = useState(false);
  const [includeBaseLinks, setIncludeBaseLinks] = useState(true);
  const [baseUrl, setBaseUrl] = useState('https://network.lnjmp.com/click.track');
  const [generatingLinks, setGeneratingLinks] = useState(false);
  const [linksResult, setLinksResult] = useState(null);
  const [mappingDrawer, setMappingDrawer] = useState({ open: false, type: 'campaign' });
  const [mappingRowIndex, setMappingRowIndex] = useState(0);

  const campaignTransactionDownloadLabel = campaignTransactionFormat.includes('csv') ? 'Run + Download CSV' : 'Run + Download Excel';
  const apiBaseUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:8147/api'}`.replace(/\/api$/, '');
  const buildApiDownloadUrl = (path) => `${apiBaseUrl}${path}`;
  const syncTabButtonClass = (tabId) => `px-3 py-2 text-sm rounded-lg border ${syncSectionTab === tabId ? 'bg-red-700/20 border-red-600/50 text-red-200' : 'bg-slate-900/40 border-slate-700 text-slate-300 hover:text-slate-100'}`;

  const hasAuthCredentials = useMemo(() => (
    String(credentials.username || '').trim().length > 0
    && String(credentials.password || '').trim().length > 0
  ), [credentials.username, credentials.password]);
  const merchantIdIsValid = useMemo(() => {
    const value = String(credentials.merchantId || '').trim();
    if (!value) return false;
    return /^\d+$/.test(value);
  }, [credentials.merchantId]);
  const hasMerchantCredentials = useMemo(() => hasAuthCredentials && merchantIdIsValid, [hasAuthCredentials, merchantIdIsValid]);

  const campaignPreview = useMemo(() => campaignMappedRows.slice(0, 8), [campaignMappedRows]);
  const creativePreview = useMemo(() => creativeMappedRows.slice(0, 8), [creativeMappedRows]);
  const visibleWorkflows = useMemo(() => workflows.filter((workflow) => workflow.mode === activeMode), [activeMode]);
  const selectedMerchantOption = useMemo(
    () => merchantOptions.find((option) => String(option.merchantId) === String(credentials.merchantId || '').trim()) || null,
    [merchantOptions, credentials.merchantId]
  );
  const advertiserScopeOptions = useMemo(() => {
    const seen = new Set();
    return (storedAdvertisers || []).reduce((acc, advertiser) => {
      const merchantId = Number(advertiser.merchant_id);
      if (!Number.isFinite(merchantId) || merchantId <= 0 || seen.has(merchantId)) return acc;
      seen.add(merchantId);
      const label = advertiser.company_name || advertiser.advertiser_name || `Merchant ${merchantId}`;
      acc.push({
        key: `merchant-${merchantId}`,
        merchantId,
        label
      });
      return acc;
    }, []);
  }, [storedAdvertisers]);
  const selectedAdvertiserScopeOption = useMemo(
    () => advertiserScopeOptions.find((option) => option.key === mappingAdvertiserScope) || null,
    [advertiserScopeOptions, mappingAdvertiserScope]
  );
  const selectedScopeKey = mappingAdvertiserScope ? `advertiser:${mappingAdvertiserScope}` : null;
  const scopedCampaignProfiles = useMemo(() => (
    (campaignProfiles || []).filter((profile) => {
      const sourceKey = profile?.source_key ? String(profile.source_key) : '';
      if (!selectedScopeKey) return !sourceKey;
      return sourceKey === selectedScopeKey;
    })
  ), [campaignProfiles, selectedScopeKey]);
  const scopedCreativeProfiles = useMemo(() => (
    (creativeProfiles || []).filter((profile) => {
      const sourceKey = profile?.source_key ? String(profile.source_key) : '';
      if (!selectedScopeKey) return !sourceKey;
      return sourceKey === selectedScopeKey;
    })
  ), [creativeProfiles, selectedScopeKey]);
  const activeMappedRows = mappingDrawer.type === 'creative' ? creativeMappedRows : campaignMappedRows;
  const activeRawRows = mappingDrawer.type === 'creative' ? creativeRows : campaignRows;
  const activeHeaders = mappingDrawer.type === 'creative' ? creativeHeaders : campaignHeaders;
  const activeFieldOverrides = mappingDrawer.type === 'creative' ? creativeFieldOverrides : campaignFieldOverrides;
  const activeFieldOptions = mappingDrawer.type === 'creative' ? creativeFieldOptions : campaignFieldOptions;
  const activeFileName = mappingDrawer.type === 'creative' ? creativeFileName : campaignFileName;
  const activeHeaderRow = mappingDrawer.type === 'creative' ? creativeHeaderRowIndex : campaignHeaderRowIndex;
  const activeMappedRow = activeMappedRows[mappingRowIndex] || null;
  const activeSourceRow = activeRawRows[mappingRowIndex] || null;

  const updateActiveFieldOverride = (header, nextValue) => {
    if (mappingDrawer.type === 'creative') {
      setCreativeFieldOverrides((state) => ({ ...state, [header]: nextValue }));
      return;
    }
    setCampaignFieldOverrides((state) => ({ ...state, [header]: nextValue }));
  };
  const campaignOptions = useMemo(() => {
    const seen = new Set();
    return (storedCampaigns || []).filter((item) => {
      const id = Number(item.linktrust_campaign_id);
      if (!Number.isFinite(id) || id <= 0) return false;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [storedCampaigns]);
  const advertiserLabelByMerchant = useMemo(() => {
    const map = {};
    (storedAdvertisers || []).forEach((advertiser) => {
      const merchantId = Number.parseInt(advertiser.merchant_id, 10);
      if (!Number.isFinite(merchantId) || merchantId <= 0 || map[merchantId]) return;
      map[merchantId] = advertiser.company_name || advertiser.advertiser_name || `Merchant ${merchantId}`;
    });
    return map;
  }, [storedAdvertisers]);
  const bulkAdvertiserOptions = useMemo(() => (
    Object.entries(advertiserLabelByMerchant)
      .map(([merchantId, label]) => ({ merchantId: Number(merchantId), label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  ), [advertiserLabelByMerchant]);
  const bulkCategoryOptions = useMemo(() => (
    Array.from(new Set(
      campaignOptions
        .map((campaign) => String(campaign.category || '').trim())
        .filter(Boolean)
    )).sort((a, b) => a.localeCompare(b))
  ), [campaignOptions]);
  const filteredCampaignOptions = useMemo(() => {
    const search = String(bulkCampaignSearch || '').trim().toLowerCase();
    return campaignOptions.filter((campaign) => {
      const campaignId = Number(campaign.linktrust_campaign_id);
      const merchantId = Number(campaign.merchant_id);
      const advertiserLabel = advertiserLabelByMerchant[merchantId] || `Merchant ${merchantId}`;
      const category = String(campaign.category || '').trim();
      if (bulkAdvertiserFilter && String(merchantId) !== String(bulkAdvertiserFilter)) return false;
      if (bulkCategoryFilter && category !== bulkCategoryFilter) return false;
      if (!search) return true;

      const haystack = [
        campaign.campaign_name,
        campaign.linktrust_campaign_id,
        advertiserLabel,
        category
      ].map((value) => String(value || '').toLowerCase()).join(' ');
      return haystack.includes(search);
    });
  }, [campaignOptions, bulkCampaignSearch, bulkAdvertiserFilter, bulkCategoryFilter, advertiserLabelByMerchant]);
  const creativeOptionsByCampaign = useMemo(() => {
    const map = {};
    (storedCreatives || []).forEach((item) => {
      const campaignId = Number(item.campaign_id);
      const adId = Number(item.ad_id);
      if (!Number.isFinite(campaignId) || campaignId <= 0 || !Number.isFinite(adId) || adId <= 0) {
        return;
      }

      if (!map[campaignId]) {
        map[campaignId] = [];
      }

      if (map[campaignId].some((existing) => existing.adId === adId)) {
        return;
      }

      map[campaignId].push({
        adId,
        label: item.creative_name || `ADID ${adId}`
      });
    });

    Object.keys(map).forEach((campaignId) => {
      map[campaignId].sort((a, b) => a.adId - b.adId);
    });

    return map;
  }, [storedCreatives]);
  const campaignAssetCoverage = useMemo(() => {
    const rows = campaignOptions.map((campaign) => {
      const campaignId = Number(campaign.linktrust_campaign_id);
      const syncedAdIds = (creativeOptionsByCampaign[String(campaignId)] || []).map((item) => item.adId);
      const selectedAdIdsForCampaign = (campaignAdSelections[String(campaignId)] || [])
        .map((value) => Number.parseInt(value, 10))
        .filter((value) => Number.isFinite(value) && value > 0);
      const selectedSet = new Set(selectedAdIdsForCampaign);

      return {
        campaignId,
        campaignName: campaign.campaign_name || `Campaign ${campaignId}`,
        syncedCount: syncedAdIds.length,
        selectedCount: selectedAdIdsForCampaign.length,
        needsManual: syncedAdIds.length === 0 && selectedAdIdsForCampaign.length === 0,
        hasManualOnly: syncedAdIds.length === 0 && selectedAdIdsForCampaign.length > 0,
        allSyncedSelected: syncedAdIds.length > 0 && syncedAdIds.every((adId) => selectedSet.has(adId))
      };
    });

    return {
      total: rows.length,
      missing: rows.filter((row) => row.needsManual).length,
      complete: rows.filter((row) => row.allSyncedSelected || row.hasManualOnly).length,
      rows
    };
  }, [campaignOptions, creativeOptionsByCampaign, campaignAdSelections]);
  const filteredCampaignAssetCoverage = useMemo(() => {
    const filteredIds = new Set(filteredCampaignOptions.map((campaign) => Number(campaign.linktrust_campaign_id)));
    const rows = campaignAssetCoverage.rows.filter((row) => filteredIds.has(Number(row.campaignId)));
    return {
      total: rows.length,
      missing: rows.filter((row) => row.needsManual).length,
      complete: rows.filter((row) => row.allSyncedSelected || row.hasManualOnly).length,
      rows
    };
  }, [campaignAssetCoverage, filteredCampaignOptions]);

  const credentialsForSync = useMemo(() => {
    const base = {
      username: String(credentials.username || '').trim(),
      password: String(credentials.password || '').trim()
    };
    if (merchantIdIsValid) {
      return { ...base, merchantId: String(credentials.merchantId || '').trim() };
    }
    return base;
  }, [credentials, merchantIdIsValid]);

  const appendAutomationLog = ({ area = 'general', level = 'info', message, details = '' }) => {
    setAutomationActivityLog((current) => {
      const entry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        ts: new Date().toISOString(),
        area,
        level,
        message: String(message || ''),
        details: String(details || '')
      };
      return [...current, entry].slice(-300);
    });
  };

  const persistAutomationProfileContext = (profile) => {
    if (!profile?.id) return;
    const merchantId = normalizeMerchantId(
      profile?.credentials?.merchantId
      || profile?.merchant_id
      || profile?.profile_payload?.syncContext?.merchantId
      || ''
    );
    const context = {
      profileId: String(profile.id),
      profileName: profile.profile_name || '',
      clientName: profile.client_name || '',
      merchantId,
      username: profile?.credentials?.username || '',
      savedAt: new Date().toISOString()
    };
    sessionStorage.setItem(ACTIVE_AUTOMATION_PROFILE_CONTEXT_KEY, JSON.stringify(context));
    setAppliedAutomationProfile(context);
  };

  const getAppliedProfileMismatch = () => {
    if (!selectedAutomationProfileId) return 'Select and apply an automation profile first.';
    if (!appliedAutomationProfile?.profileId) return 'Apply the selected automation profile before running sync/export actions.';
    if (String(appliedAutomationProfile.profileId) !== String(selectedAutomationProfileId)) {
      return 'The selected profile has not been applied yet. Click Apply before syncing.';
    }
    if (appliedAutomationProfile.username && String(appliedAutomationProfile.username).trim() !== String(credentials.username || '').trim()) {
      return `Credential mismatch: applied profile user is ${appliedAutomationProfile.username}, but the form currently has ${credentials.username || 'blank'}. Click Apply again.`;
    }
    if (appliedAutomationProfile.merchantId && normalizeMerchantId(appliedAutomationProfile.merchantId) !== normalizeMerchantId(credentials.merchantId)) {
      return `Merchant mismatch: applied profile merchant is ${appliedAutomationProfile.merchantId}, but the form currently has ${credentials.merchantId || 'blank'}. Click Apply again.`;
    }
    return '';
  };

  const requireAppliedProfileContext = (area = 'sync') => {
    const mismatch = getAppliedProfileMismatch();
    if (!mismatch) return true;
    setError(mismatch);
    appendAutomationLog({ area, level: 'error', message: 'Blocked action because profile context is not applied.', details: mismatch });
    return false;
  };

  const loadAppliedProfileCredentialsForAction = async (area = 'sync') => {
    if (!requireAppliedProfileContext(area)) return null;
    const response = await adminReportingAPI.getLinkTrustAutomationProfile(selectedAutomationProfileId);
    const profile = response?.data?.profile;
    const creds = profile?.credentials || {};
    const username = String(creds.username || '').trim();
    const password = String(creds.password || '').trim();
    const merchantId = normalizeMerchantId(creds.merchantId || profile?.merchant_id || profile?.profile_payload?.syncContext?.merchantId || '');
    if (!username || !password) {
      throw new Error('Applied automation profile is missing LinkTrust username/password. Re-save the profile credentials.');
    }
    if (String(profile?.id || '') !== String(selectedAutomationProfileId)) {
      throw new Error('Loaded automation profile does not match the selected profile. Re-apply the profile.');
    }
    return {
      username,
      password,
      ...(merchantId ? { merchantId } : {})
    };
  };

  const bindAppliedProfileToDiscoveredAccount = async (merchantIds = [], area = 'campaign-sync') => {
    const uniqueIds = Array.from(new Set(
      (merchantIds || [])
        .map((value) => Number.parseInt(String(value || ''), 10))
        .filter((value) => Number.isFinite(value) && value > 0)
    ));
    if (uniqueIds.length !== 1 || normalizeMerchantId(credentials.merchantId)) return null;

    const discoveredMerchantId = String(uniqueIds[0]);
    const response = await adminReportingAPI.updateLinkTrustAutomationProfileContext(selectedAutomationProfileId, {
      merchantId: discoveredMerchantId
    });
    const profile = response?.data?.profile;
    if (profile) {
      setCredentials((state) => ({ ...state, merchantId: discoveredMerchantId }));
      persistAutomationProfileContext(profile);
      appendAutomationLog({
        area,
        level: 'success',
        message: 'Automation profile context was bound to the Campaign Listing account ID.',
        details: `Profile #${selectedAutomationProfileId} · account/advertiser ${discoveredMerchantId}`
      });
      await loadAutomationProfiles(discoveredMerchantId);
      return discoveredMerchantId;
    }
    return null;
  };

  const loadMerchantOptions = async () => {
    const response = await adminReportingAPI.listLinkTrustMerchantOptions();
    const merchants = response?.data?.merchants || [];
    setMerchantOptions(merchants);
  };

  const loadMappingProfiles = async (merchantId) => {
    const merchantParam = /^\d+$/.test(String(merchantId || '')) ? { merchant_id: String(merchantId) } : {};
    const [campaignResponse, creativeResponse] = await Promise.all([
      adminReportingAPI.listLinkTrustMappingProfiles({ profile_type: 'campaign', ...merchantParam, limit: 200 }),
      adminReportingAPI.listLinkTrustMappingProfiles({ profile_type: 'creative', ...merchantParam, limit: 200 })
    ]);
    setCampaignProfiles(campaignResponse?.data?.profiles || []);
    setCreativeProfiles(creativeResponse?.data?.profiles || []);
  };

  const loadAutomationProfiles = async (merchantId) => {
    const merchantParam = /^\d+$/.test(String(merchantId || '')) ? { merchant_id: String(merchantId) } : {};
    const response = await adminReportingAPI.listLinkTrustAutomationProfiles({ ...merchantParam, limit: 500 });
    setAutomationProfiles(response?.data?.profiles || []);
  };

  const loadStoredForMerchant = async (merchantId) => {
    const merchantFilter = merchantId && /^\d+$/.test(String(merchantId))
      ? { merchant_id: merchantId }
      : {};

    const [campaignsResponse, advertisersResponse, affiliatesResponse, creativesResponse, linksResponse] = await Promise.all([
      adminReportingAPI.listLinkTrustCampaigns({ ...merchantFilter, limit: 500 }),
      adminReportingAPI.listLinkTrustAdvertisers(merchantFilter),
      adminReportingAPI.listLinkTrustAffiliates({ ...merchantFilter, limit: 1500 }),
      adminReportingAPI.listLinkTrustCreatives({ ...merchantFilter, limit: 1500 }),
      merchantFilter.merchant_id
        ? adminReportingAPI.listLinkTrustLinks({ merchant_id: merchantId, limit: 500 })
        : Promise.resolve({ data: { links: [] } })
    ]);

    setStoredCampaigns(campaignsResponse?.data?.campaigns || []);
    setStoredAdvertisers(advertisersResponse?.data?.advertisers || []);
    setStoredAffiliates(affiliatesResponse?.data?.affiliates || []);
    setStoredCreatives(creativesResponse?.data?.creatives || []);
    setStoredLinks(linksResponse?.data?.links || []);
  };

  const refreshAll = async () => {
    try {
      setError('');
      setRefreshing(true);
      await loadMerchantOptions();
      await loadMappingProfiles(String(credentials.merchantId || '').trim());
      await loadAutomationProfiles(String(credentials.merchantId || '').trim());
      await loadStoredForMerchant(String(credentials.merchantId || '').trim());
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Failed to load automations workspace'));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(LINKTRUST_SESSION_CREDENTIALS_KEY);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (!parsed || typeof parsed !== 'object') return;
      setCredentials((state) => ({
        ...state,
        ...parsed,
        clickListingApiUrl: LOCKED_CLICK_LISTING_API_URL
      }));
    } catch {
      sessionStorage.removeItem(LINKTRUST_SESSION_CREDENTIALS_KEY);
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem(LINKTRUST_SESSION_CREDENTIALS_KEY, JSON.stringify(credentials));
  }, [credentials]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(ACTIVE_AUTOMATION_PROFILE_CONTEXT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return;
      if (parsed.profileId) setSelectedAutomationProfileId(String(parsed.profileId));
      setAppliedAutomationProfile(parsed);
    } catch {
      sessionStorage.removeItem(ACTIVE_AUTOMATION_PROFILE_CONTEXT_KEY);
    }
  }, []);

  useEffect(() => {
    const end = new Date();
    const start = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000);
    const toDate = (value) => {
      const local = new Date(value.getTime() - (value.getTimezoneOffset() * 60 * 1000));
      return local.toISOString().slice(0, 10);
    };
    setLeadParams({ startDate: toDate(start), endDate: toDate(end), trafficFilterLeadStats: '1' });
    setCampaignTransactionParams((state) => ({
      ...state,
      startDate: toDate(start),
      endDate: toDate(end)
    }));
    setClickListingParams((state) => ({
      ...state,
      startDate: toDate(start),
      endDate: toDate(end)
    }));
  }, []);

  useEffect(() => {
    loadMerchantOptions().catch((requestError) => {
      setError(toDisplayError(requestError, 'Failed to load merchant options'));
    });
  }, []);

  useEffect(() => {
    const allowed = workflows.filter((workflow) => workflow.mode === activeMode).map((workflow) => workflow.id);
    if (!allowed.includes(activeWorkflow)) {
      setActiveWorkflow(allowed[0] || 'sync');
    }
  }, [activeMode, activeWorkflow]);

  useEffect(() => {
    if (!merchantIdIsValid) {
      setSelectedCampaignIds([]);
      setSelectedAffiliateIds([]);
      setSelectedAdIds([]);
      setCampaignAdSelections({});
      setCampaignAdDrafts({});
      setCampaignProfiles([]);
      setCreativeProfiles([]);
      setStoredCampaigns([]);
      setStoredAdvertisers([]);
      setStoredAffiliates([]);
      setStoredCreatives([]);
      setStoredLinks([]);
      return;
    }

    const timer = setTimeout(() => {
      setSelectedCampaignIds([]);
      setSelectedAffiliateIds([]);
      setSelectedAdIds([]);
      setCampaignAdSelections({});
      setCampaignAdDrafts({});
      loadMappingProfiles(String(credentials.merchantId || '').trim()).catch((requestError) => {
        setError(toDisplayError(requestError, 'Failed to load mapping profiles'));
      });
      loadAutomationProfiles(String(credentials.merchantId || '').trim()).catch((requestError) => {
        setError(toDisplayError(requestError, 'Failed to load automation profiles'));
      });
      loadStoredForMerchant(String(credentials.merchantId || '').trim()).catch((requestError) => {
        setError(toDisplayError(requestError, 'Failed to load stored merchant data'));
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [credentials.merchantId, merchantIdIsValid]);

  useEffect(() => {
    if (!uploadedAssets.length) {
      setScannedCreativeCandidates([]);
      setScannedCreativeRows([]);
      return;
    }
    const candidates = buildScannedCreativeCandidatesFromAssets({
      assets: uploadedAssets,
      campaignOptions
    });
    setScannedCreativeCandidates(candidates);
  }, [uploadedAssets, campaignOptions]);

  useEffect(() => {
    setScannedCreativeRows(mapScannedCreativeCandidates({
      candidates: scannedCreativeCandidates,
      scannerMapper
    }));
  }, [scannedCreativeCandidates, scannerMapper]);

  useEffect(() => {
    setSelectedCampaignProfileId('');
    setSelectedCreativeProfileId('');
  }, [mappingAdvertiserScope]);

  useEffect(() => {
    if (!clickListingJobRunId) return undefined;

    let active = true;
    const poll = async () => {
      try {
        const [response, jobsResponse] = await Promise.all([
          adminReportingAPI.getLinkTrustClickListingJob(clickListingJobRunId),
          adminReportingAPI.listLinkTrustClickListingJobs({ limit: 30 })
        ]);
        if (!active) return;
        const run = response?.data?.run || null;
        setClickListingJobRun(run);
        setClickListingJobs(jobsResponse?.data?.runs || []);
        if (run?.status === 'completed' || run?.status === 'failed') {
          return;
        }
      } catch (requestError) {
        if (!active) return;
        setError(toDisplayError(requestError, 'Failed to poll click listing job status'));
      }
    };

    poll();
    const timer = setInterval(poll, 5000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [clickListingJobRunId]);

  useEffect(() => {
    let active = true;
    adminReportingAPI.listLinkTrustClickListingJobs({ limit: 30 })
      .then((response) => {
        if (!active) return;
        setClickListingJobs(response?.data?.runs || []);
      })
      .catch(() => {
        // ignore initial load errors
      });
    return () => { active = false; };
  }, []);

  const handleCampaignFile = async (event) => {
    const [file] = event.target.files || [];
    if (!file) return;
    try {
      setError('');
      const parsed = await parseImportFile(file);
      setCampaignRows(parsed.rows || []);
      setCampaignHeaders(parsed.headers || []);
      const initialOverrides = (parsed.headers || []).reduce((acc, header) => {
        acc[header] = suggestCampaignFieldByHeader(header);
        return acc;
      }, {});
      setCampaignFieldOverrides(initialOverrides);
      setCampaignMappedRows(parsed.mappedRows || []);
      setCampaignHeaderRowIndex(Number(parsed.headerRowIndex || 0));
      setCampaignFileName(file.name);
      setCampaignResult(null);
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Could not parse campaign file'));
    }
  };

  const runCampaignImport = async () => {
    if (!hasMerchantCredentials) {
      setError('Enter username, password, and merchant ID first.');
      return;
    }
    if (!campaignRows.length) {
      setError('Upload a campaign file first.');
      return;
    }

    try {
      setError('');
      setCampaignImporting(true);
      const preparedRows = remapRowsByFieldOverrides(campaignRows, campaignFieldOverrides);
      const response = await adminReportingAPI.importLinkTrustCampaigns({
        rows: preparedRows,
        credentials,
        options: {
          dryRun: campaignDryRun,
          stopOnError: campaignStopOnError,
          requiredOnly: true,
          ...(campaignCategoryOverride.trim() ? { categoryOverride: campaignCategoryOverride.trim() } : {})
        }
      });
      setCampaignResult(response?.data || null);
      await refreshAll();
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Campaign import failed'));
    } finally {
      setCampaignImporting(false);
    }
  };

  const handleCreativeFile = async (event) => {
    const [file] = event.target.files || [];
    if (!file) return;
    try {
      setError('');
      const parsed = await parseCreativeImportFile(file);
      setCreativeRows(parsed.rows || []);
      setCreativeHeaders(parsed.headers || []);
      const initialOverrides = (parsed.headers || []).reduce((acc, header) => {
        acc[header] = suggestCreativeFieldByHeader(header);
        return acc;
      }, {});
      setCreativeFieldOverrides(initialOverrides);
      setCreativeMappedRows(parsed.mappedRows || []);
      setCreativeHeaderRowIndex(Number(parsed.headerRowIndex || 0));
      setCreativeFileName(file.name);
      setCreativeResult(null);
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Could not parse creative file'));
    }
  };

  const handleAssetUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    try {
      setError('');
      setUploadingAssets(true);
      const response = await adminReportingAPI.uploadLinkTrustCreativeAssets(files);
      const assets = response?.data?.assets || [];
      const normalizedAssets = assets.map((asset, index) => ({
        ...asset,
        localPath: asset.localPath || files[index]?.webkitRelativePath || files[index]?.name || asset.originalName
      }));
      let scannerCandidates = [];
      setUploadedAssets((current) => {
        const combinedAssets = [...current, ...normalizedAssets];
        scannerCandidates = buildScannedCreativeCandidatesFromAssets({
          assets: combinedAssets,
          campaignOptions
        });
        return combinedAssets;
      });
      setScannedCreativeCandidates(scannerCandidates);

      if (!creativeRows.length) {
        const syntheticRows = mapScannedCreativeCandidates({
          candidates: scannerCandidates,
          scannerMapper
        });

        const headers = ['name', 'campaignName', 'campaignId', 'adId', 'fileName', 'creativeType', 'fileOrImageUrl'];
        const overrides = headers.reduce((acc, header) => {
          acc[header] = suggestCreativeFieldByHeader(header);
          return acc;
        }, {});

        setCreativeRows(syntheticRows);
        setCreativeHeaders(headers);
        setCreativeFieldOverrides(overrides);
        setCreativeMappedRows(syntheticRows.map((row, rowIndex) => ({ index: rowIndex, source: row, mapped: row, leftovers: [] })));
        setCreativeFileName(`Generated from ${normalizedAssets.length} uploaded asset(s)`);
      }
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Asset upload failed'));
    } finally {
      setUploadingAssets(false);
    }
  };

  const updateScannedCreativeRow = (rowIndex, field, value) => {
    setScannedCreativeRows((rows) => rows.map((row, index) => (
      index === rowIndex ? { ...row, [field]: value } : row
    )));
  };

  const applyScannedRowsToCreativeImport = () => {
    if (!scannedCreativeRows.length) return;
    const headers = ['name', 'campaignName', 'campaignId', 'adId', 'fileName', 'creativeType', 'fileOrImageUrl'];
    const overrides = headers.reduce((acc, header) => {
      acc[header] = suggestCreativeFieldByHeader(header);
      return acc;
    }, {});

    setCreativeRows(scannedCreativeRows);
    setCreativeHeaders(headers);
    setCreativeFieldOverrides(overrides);
    setCreativeMappedRows(scannedCreativeRows.map((row, rowIndex) => ({ index: rowIndex, source: row, mapped: row, leftovers: [] })));
    setCreativeFileName(`Scanned from ${scannedCreativeRows.length} uploaded asset(s)`);
  };

  const runCreativeImport = async () => {
    if (!creativeLocalOnly && !hasAuthCredentials) {
      setError('Enter username and password first. Merchant ID is optional for creative import.');
      return;
    }
    if (!creativeRows.length) {
      setError('Upload a creative file first.');
      return;
    }

    const assetMap = uploadedAssets.reduce((acc, item) => {
      if (item?.originalName && item?.url) acc[item.originalName] = item.url;
      if (item?.storedName && item?.url) acc[item.storedName] = item.url;
      if (item?.localPath && item?.url) {
        acc[item.localPath] = item.url;
        acc[String(item.localPath).toLowerCase()] = item.url;
      }
      return acc;
    }, {});

    try {
      setError('');
      setCreativeImporting(true);
      const preparedRows = remapRowsByFieldOverrides(creativeRows, creativeFieldOverrides);
      const response = await adminReportingAPI.importLinkTrustCreatives({
        rows: preparedRows,
        credentials: credentialsForSync,
        assetMap,
        options: {
          dryRun: creativeDryRun,
          localOnly: creativeLocalOnly,
          stopOnError: false,
          ...(creativeCampaignOverride ? { campaignIdOverride: creativeCampaignOverride } : {})
        }
      });
      setCreativeResult(response?.data || null);
      await refreshAll();
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Creative import failed'));
    } finally {
      setCreativeImporting(false);
    }
  };

  const syncAffiliates = async () => {
    if (!requireAppliedProfileContext('affiliate-sync')) return;
    if (!hasAuthCredentials) {
      setError('Enter username and password first. Merchant ID is optional for this sync.');
      return;
    }
    try {
      setSyncingAffiliates(true);
      setError('');
      appendAutomationLog({ area: 'affiliate-sync', message: 'Affiliate directory sync started.' });
      const response = await adminReportingAPI.syncLinkTrustAffiliates({
        credentials: credentialsForSync,
        options: { includeAffiliateCampaignData: false }
      });
      setAffiliateSyncResult(response?.data || null);
      appendAutomationLog({
        area: 'affiliate-sync',
        level: 'success',
        message: 'Affiliate directory sync completed.',
        details: `Stored ${(response?.data?.data?.storedCount || response?.data?.storedCount || 0)} row(s).`
      });
      await refreshAll();
    } catch (requestError) {
      appendAutomationLog({ area: 'affiliate-sync', level: 'error', message: 'Affiliate sync failed.', details: toDisplayError(requestError, 'Affiliate sync failed') });
      setError(toDisplayError(requestError, 'Affiliate sync failed'));
    } finally {
      setSyncingAffiliates(false);
    }
  };

  const syncAdvertisers = async () => {
    if (!requireAppliedProfileContext('advertiser-sync')) return;
    if (!hasAuthCredentials) {
      setError('Enter username and password first. Merchant ID is optional for this sync.');
      return;
    }
    try {
      setSyncingAdvertisers(true);
      setError('');
      appendAutomationLog({ area: 'advertiser-sync', message: 'Advertiser directory sync started.' });
      const response = await adminReportingAPI.syncLinkTrustAdvertisers({ credentials: credentialsForSync });
      setAdvertiserSyncResult(response?.data || null);
      appendAutomationLog({
        area: 'advertiser-sync',
        level: 'success',
        message: 'Advertiser directory sync completed.',
        details: `Stored ${(response?.data?.data?.storedCount || response?.data?.storedCount || 0)} row(s).`
      });
      await refreshAll();
    } catch (requestError) {
      appendAutomationLog({ area: 'advertiser-sync', level: 'error', message: 'Advertiser sync failed.', details: toDisplayError(requestError, 'Advertiser sync failed') });
      setError(toDisplayError(requestError, 'Advertiser sync failed'));
    } finally {
      setSyncingAdvertisers(false);
    }
  };

  const syncCampaigns = async () => {
    if (!hasAuthCredentials) {
      setError('Enter username and password first. Merchant ID is optional for this sync.');
      return;
    }
    try {
      setSyncingCampaigns(true);
      setError('');
      const profileCredentials = await loadAppliedProfileCredentialsForAction('campaign-sync');
      if (!profileCredentials) return;
      appendAutomationLog({
        area: 'campaign-sync',
        message: 'Campaign listing fetch started using freshly loaded applied profile credentials.',
        details: `profile #${selectedAutomationProfileId} · user ${profileCredentials.username} · merchant ${profileCredentials.merchantId || 'none'}`
      });
      const response = await adminReportingAPI.syncLinkTrustCampaigns({
        credentials: profileCredentials,
        options: { previewOnly: true }
      });
      setCampaignSyncResult(response?.data || null);
      setCampaignSyncPreview(response?.data || null);
      const syncData = response?.data || {};
      appendAutomationLog({
        area: 'campaign-sync',
        level: (syncData.skippedCount || 0) > 0 ? 'warn' : 'success',
        message: 'Campaign listing fetched for review.',
        details: `URL ${syncData.endpoint || 'n/a'} · user ${syncData.authUsername || profileCredentials.username || 'n/a'} · expected merchant ${syncData.merchantIdSeed || profileCredentials.merchantId || 'none'} · saveable ${(syncData.saveableCount || 0)} of ${(syncData.campaignCount || 0)} row(s) · skipped ${(syncData.skippedCount || 0)} · returned merchant IDs ${(syncData.merchantIds || []).join(', ') || 'none'}.`
      });
      if ((syncData.skippedMerchantMismatchCount || 0) > 0) {
        appendAutomationLog({
          area: 'campaign-sync',
          level: 'error',
          message: 'Campaign sync skipped rows because returned campaign merchant IDs did not match the active profile merchant.',
          details: `Mismatches: ${syncData.skippedMerchantMismatchCount}. First sample: ${JSON.stringify((syncData.skippedSamples || [])[0] || {})}`
        });
      }
      const boundMerchantId = await bindAppliedProfileToDiscoveredAccount(syncData.merchantIds || [], 'campaign-sync');
      if (boundMerchantId) {
        await loadMappingProfiles(boundMerchantId);
        await loadStoredForMerchant(boundMerchantId);
      } else {
        await refreshAll();
      }
    } catch (requestError) {
      appendAutomationLog({ area: 'campaign-sync', level: 'error', message: 'Campaign sync failed.', details: toDisplayError(requestError, 'Campaign sync failed') });
      setError(toDisplayError(requestError, 'Campaign sync failed'));
    } finally {
      setSyncingCampaigns(false);
    }
  };

  const confirmSaveCampaignSyncPreview = async () => {
    const previewRows = campaignSyncPreview?.previewRows || [];
    if (!previewRows.length) {
      setError('Fetch campaign listing first, then confirm save.');
      return;
    }
    try {
      setSavingCampaignPreview(true);
      setError('');
      const profileCredentials = await loadAppliedProfileCredentialsForAction('campaign-sync');
      if (!profileCredentials) return;
      appendAutomationLog({
        area: 'campaign-sync',
        message: 'Saving confirmed campaign listing preview.',
        details: `${previewRows.length} campaign row(s) selected for save.`
      });
      const response = await adminReportingAPI.syncLinkTrustCampaigns({
        credentials: profileCredentials,
        options: { confirmRows: previewRows }
      });
      const syncData = response?.data || null;
      setCampaignSyncResult(syncData);
      setCampaignSyncPreview(null);
      appendAutomationLog({
        area: 'campaign-sync',
        level: 'success',
        message: 'Confirmed campaign listing saved.',
        details: `Stored ${(syncData?.storedCount || 0)} row(s) · skipped ${(syncData?.skippedCount || 0)} · merchant IDs ${(syncData?.merchantIds || []).join(', ') || 'none'}.`
      });
      await refreshAll();
    } catch (requestError) {
      appendAutomationLog({ area: 'campaign-sync', level: 'error', message: 'Failed to save confirmed campaign listing.', details: toDisplayError(requestError, 'Campaign preview save failed') });
      setError(toDisplayError(requestError, 'Campaign preview save failed'));
    } finally {
      setSavingCampaignPreview(false);
    }
  };

  const syncCreatives = async ({ campaignIds = [], creativeIds = [] } = {}) => {
    if (!requireAppliedProfileContext('creative-sync')) return;
    if (!hasAuthCredentials) {
      setError('Enter username and password first. Merchant ID is optional for this sync.');
      return;
    }
    try {
      setSyncingCreatives(true);
      setError('');
      appendAutomationLog({ area: 'creative-sync', message: 'Creative directory sync started.' });
      const response = await adminReportingAPI.syncLinkTrustCreatives({
        credentials: credentialsForSync,
        options: {
          ...(campaignIds.length ? { campaignIds } : {}),
          ...(creativeIds.length ? { creativeIds } : {})
        }
      });
      setCreativeSyncResult(response?.data || null);
      appendAutomationLog({
        area: 'creative-sync',
        level: 'success',
        message: 'Creative directory sync completed.',
        details: `Stored ${(response?.data?.data?.storedCount || response?.data?.storedCount || 0)} row(s).`
      });
      await refreshAll();
    } catch (requestError) {
      appendAutomationLog({ area: 'creative-sync', level: 'error', message: 'Creative sync failed.', details: toDisplayError(requestError, 'Creative sync failed') });
      setError(toDisplayError(requestError, 'Creative sync failed'));
    } finally {
      setSyncingCreatives(false);
    }
  };

  const syncLeadStatus = async () => {
    if (!hasMerchantCredentials) {
      setError('Enter username, password, and merchant ID first.');
      return;
    }
    try {
      setSyncingLeadStatus(true);
      setError('');
      appendAutomationLog({ area: 'lead-status-sync', message: `Lead status sync started (${leadParams.startDate} to ${leadParams.endDate}).` });
      const response = await adminReportingAPI.syncLinkTrustAffiliateLeadStatus({ credentials, params: leadParams });
      setLeadSyncResult(response?.data || null);
      appendAutomationLog({ area: 'lead-status-sync', level: 'success', message: 'Lead status sync completed.' });
      await refreshAll();
    } catch (requestError) {
      appendAutomationLog({ area: 'lead-status-sync', level: 'error', message: 'Lead status sync failed.', details: toDisplayError(requestError, 'Lead status sync failed') });
      setError(toDisplayError(requestError, 'Lead status sync failed'));
    } finally {
      setSyncingLeadStatus(false);
    }
  };

  const syncClickListing = async () => {
    if (!requireAppliedProfileContext('click-export')) return;
    if (!hasAuthCredentials) {
      setError('Enter username and password first.');
      return;
    }
    if (!clickListingParams.startDate || !clickListingParams.endDate) {
      setError('Start date and end date are required for click listing export.');
      return;
    }

    const toOptionalInt = (value) => {
      const parsed = Number.parseInt(String(value || '').trim(), 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    };

    const parsedCampaignIds = parseNumericIdList(clickListingParams.campaignIdsText);
    const params = {
      startDate: clickListingParams.startDate,
      endDate: clickListingParams.endDate,
      outputMode: clickListingFormat,
      useStoredCampaigns: Boolean(clickListingParams.useStoredCampaigns),
      ...(merchantIdIsValid ? { merchantId: Number.parseInt(String(credentials.merchantId).trim(), 10) } : {}),
      autoResumeEnabled: clickListingParams.autoResumeEnabled !== false,
      autoResumeStallThresholdSeconds: Number.parseInt(String(clickListingParams.autoResumeStallThresholdSeconds || '').trim(), 10) || 480,
      ...(parsedCampaignIds.length ? { campaignIds: parsedCampaignIds } : {}),
      ...(toOptionalInt(clickListingParams.affiliateId) ? { affiliateId: toOptionalInt(clickListingParams.affiliateId) } : {})
    };

    try {
      setSyncingClickListing(true);
      setError('');
      appendAutomationLog({ area: 'click-export', message: `Click export started for ${params.startDate} to ${params.endDate}.` });
      const response = await adminReportingAPI.startLinkTrustClickListingJob({
        credentials: {
          username: credentials.username,
          password: credentials.password,
          clickListingApiUrl: LOCKED_CLICK_LISTING_API_URL
        },
        params
      });

      const runId = response?.data?.runId || response?.data?.run?.id || null;
      setClickListingResult(response?.data || null);
      setClickListingJobRunId(runId);
      setClickListingJobRun(response?.data?.run || null);
      appendAutomationLog({ area: 'click-export', level: 'success', message: `Click export job started${runId ? ` (run #${runId})` : ''}.` });
    } catch (requestError) {
      appendAutomationLog({ area: 'click-export', level: 'error', message: 'Click listing export failed to start.', details: toDisplayError(requestError, 'Click listing export failed') });
      setError(toDisplayError(requestError, 'Click listing export failed'));
    } finally {
      setSyncingClickListing(false);
    }
  };

  const controlClickListingJob = async (action) => {
    const runId = clickListingJobRun?.id || clickListingJobRunId;
    if (!runId) {
      setError('No active click listing job to control.');
      return;
    }
    try {
      setError('');
      await adminReportingAPI.controlLinkTrustClickListingJob(runId, action);
      const refreshed = await adminReportingAPI.getLinkTrustClickListingJob(runId);
      setClickListingJobRun(refreshed?.data?.run || null);
      appendAutomationLog({ area: 'click-export', level: 'success', message: `Click export job #${runId}: ${action}.` });
    } catch (requestError) {
      appendAutomationLog({ area: 'click-export', level: 'error', message: `Failed to ${action} click export job #${runId}.`, details: toDisplayError(requestError, `Failed to ${action} click listing job`) });
      setError(toDisplayError(requestError, `Failed to ${action} click listing job`));
    }
  };

  const retryFailedClickListingJob = async () => {
    const sourceRunId = clickListingJobRun?.id || clickListingJobRunId;
    if (!sourceRunId) {
      setError('No click listing job selected for retry.');
      return;
    }
    if (!hasAuthCredentials) {
      setError('Enter username and password first.');
      return;
    }
    if (!requireAppliedProfileContext('click-export')) return;

    try {
      setError('');
      appendAutomationLog({ area: 'click-export', message: `Retrying failed click export scopes from run #${sourceRunId}.` });
      const response = await adminReportingAPI.retryFailedLinkTrustClickListingJob(sourceRunId, {
        credentials: {
          username: credentials.username,
          password: credentials.password,
          ...(String(credentials.clickListingApiUrl || '').trim() ? { clickListingApiUrl: String(credentials.clickListingApiUrl).trim() } : {})
        }
      });
      const nextRunId = response?.data?.runId || response?.data?.run?.id || null;
      setClickListingJobRunId(nextRunId);
      setClickListingJobRun(response?.data?.run || null);
      appendAutomationLog({ area: 'click-export', level: 'success', message: `Retry job started${nextRunId ? ` (run #${nextRunId})` : ''}.` });
    } catch (requestError) {
      appendAutomationLog({ area: 'click-export', level: 'error', message: 'Failed to retry click export scopes.', details: toDisplayError(requestError, 'Failed to retry failed click listing scopes') });
      setError(toDisplayError(requestError, 'Failed to retry failed click listing scopes'));
    }
  };

  const resumeClickListingJobFromCheckpoint = async () => {
    const sourceRunId = clickListingJobRun?.id || clickListingJobRunId;
    if (!sourceRunId) {
      setError('No click listing job selected for resume.');
      return;
    }
    if (!hasAuthCredentials) {
      setError('Enter username and password first.');
      return;
    }
    if (!requireAppliedProfileContext('click-export')) return;
    try {
      setError('');
      appendAutomationLog({ area: 'click-export', message: `Checkpoint resume requested from run #${sourceRunId}.` });
      const response = await adminReportingAPI.resumeLinkTrustClickListingJobFromCheckpoint(sourceRunId, {
        credentials: {
          username: credentials.username,
          password: credentials.password,
          ...(String(credentials.clickListingApiUrl || '').trim() ? { clickListingApiUrl: String(credentials.clickListingApiUrl).trim() } : {})
        }
      });
      const nextRunId = response?.data?.runId || response?.data?.run?.id || null;
      setClickListingJobRunId(nextRunId);
      setClickListingJobRun(response?.data?.run || null);
      appendAutomationLog({ area: 'click-export', level: 'success', message: `Resume job started${nextRunId ? ` (run #${nextRunId})` : ''}.` });
    } catch (requestError) {
      appendAutomationLog({ area: 'click-export', level: 'error', message: 'Failed to resume click export job.', details: toDisplayError(requestError, 'Failed to resume click listing job from checkpoint') });
      setError(toDisplayError(requestError, 'Failed to resume click listing job from checkpoint'));
    }
  };

  const mergeTransactionRunResults = (results = [], fallbackMeta = {}) => {
    const all = Array.isArray(results) ? results : [];
    const merged = {
      campaignIdSource: 'chunked_input',
      endpointBase: fallbackMeta.endpointBase || '',
      authUsername: fallbackMeta.authUsername || '',
      merchantId: fallbackMeta.merchantId || null,
      strictThrottle: fallbackMeta.strictThrottle !== false,
      requestMinIntervalMs: fallbackMeta.requestMinIntervalMs || 0,
      startDate: fallbackMeta.startDate || '',
      endDate: fallbackMeta.endDate || '',
      campaignCount: 0,
      campaignIds: [],
      successfulCampaignCount: 0,
      zeroRowCampaignCount: 0,
      zeroRowCampaigns: [],
      transactionCount: 0,
      failedCount: 0,
      failureStatusCounts: {},
      rowsByCampaign: [],
      failedCampaigns: [],
      transactions: []
    };

    const allCampaignIds = new Set();
    const zeroRowSet = new Set();
    all.forEach((result) => {
      (result?.campaignIds || []).forEach((id) => allCampaignIds.add(Number(id)));
      (result?.zeroRowCampaigns || []).forEach((id) => zeroRowSet.add(Number(id)));
      merged.transactions.push(...(result?.transactions || []));
      merged.rowsByCampaign.push(...(result?.rowsByCampaign || []));
      merged.failedCampaigns.push(...(result?.failedCampaigns || []));
      Object.entries(result?.failureStatusCounts || {}).forEach(([code, count]) => {
        merged.failureStatusCounts[code] = (merged.failureStatusCounts[code] || 0) + Number(count || 0);
      });
      if (!merged.endpointBase && result?.endpointBase) merged.endpointBase = result.endpointBase;
      if (!merged.authUsername && result?.authUsername) merged.authUsername = result.authUsername;
      if (!merged.merchantId && result?.merchantId) merged.merchantId = result.merchantId;
      if (!merged.startDate && result?.startDate) merged.startDate = result.startDate;
      if (!merged.endDate && result?.endDate) merged.endDate = result.endDate;
    });

    merged.campaignIds = Array.from(allCampaignIds).filter((id) => Number.isFinite(id) && id > 0);
    merged.campaignCount = merged.campaignIds.length;
    merged.zeroRowCampaigns = Array.from(zeroRowSet).filter((id) => Number.isFinite(id) && id > 0);
    merged.zeroRowCampaignCount = merged.zeroRowCampaigns.length;
    merged.failedCount = merged.failedCampaigns.length;
    merged.successfulCampaignCount = Math.max(0, merged.campaignCount - merged.failedCount);
    merged.transactionCount = merged.transactions.length;
    return merged;
  };

  const exportCampaignTransactionsResultFiles = (result) => {
    const endStamp = new Date().toISOString().slice(0, 10);
    const sourceRows = result?.transactions || [];
    const lt7ExactLike = ['lt7_transaction_details', 'lt7_transaction_details_excel_raw', 'lt7_transaction_details_csv', 'lt7_transaction_details_csv_raw'];
    const includeRawRemainder = ['lt7_transaction_details_excel_raw', 'lt7_transaction_details_csv_raw'].includes(campaignTransactionFormat);
    const summaryRows = summarizeTransactionRows(sourceRows);
    const exportRows = lt7ExactLike.includes(campaignTransactionFormat)
      ? formatLt7TransactionDetailsRows(sourceRows, { includeRawRemainder })
      : sourceRows;
    const formatSuffix = campaignTransactionFormat === 'lt7_transaction_details'
      ? 'lt7-transaction-details'
      : campaignTransactionFormat === 'lt7_transaction_details_excel_raw'
        ? 'lt7-transaction-details-excel-raw'
      : campaignTransactionFormat === 'lt7_transaction_details_csv'
        ? 'lt7-transaction-details-csv'
        : campaignTransactionFormat === 'lt7_transaction_details_csv_raw'
          ? 'lt7-transaction-details-csv-raw'
        : 'raw';
    if (campaignTransactionFormat === 'raw_and_summary_csv') {
      downloadCsv(
        `grab-all-campaign-transactions-raw-${endStamp}.csv`,
        sourceRows,
        Object.keys(sourceRows[0] || { info: '' })
      );
      downloadCsv(
        `grab-all-campaign-transactions-summary-${endStamp}.csv`,
        summaryRows,
        ['CID', 'AffiliateId', 'Status', 'Transactions', 'Revenue', 'Commission']
      );
    } else if (campaignTransactionFormat === 'lt7_transaction_details_csv' || campaignTransactionFormat === 'lt7_transaction_details_csv_raw') {
      downloadCsv(
        `grab-all-campaign-transactions-${formatSuffix}-${endStamp}.csv`,
        exportRows,
        campaignTransactionFormat === 'lt7_transaction_details_csv_raw'
          ? buildLt7RawTailColumns(sourceRows)
          : LT7_TRANSACTION_DETAIL_COLUMNS
      );
    } else {
      downloadWorkbook(`grab-all-campaign-transactions-${formatSuffix}-${endStamp}.xlsx`, exportRows);
    }
  };

  const syncCampaignTransactions = async () => {
    if (!requireAppliedProfileContext('transactions-export')) return;
    if (!hasAuthCredentials) {
      setError('Enter username and password first. Merchant ID is optional unless using merchant-scoped stored campaigns.');
      return;
    }

    const parsedCampaignIds = parseNumericIdList(campaignTransactionParams.campaignIdsText);
    if (!parsedCampaignIds.length && !campaignTransactionParams.useStoredCampaigns) {
      setError('Provide at least one campaign ID or enable use stored campaigns.');
      return;
    }

    const storedCampaignIdSet = new Set(campaignOptions
      .map((campaign) => Number.parseInt(String(campaign.linktrust_campaign_id || ''), 10))
      .filter((value) => Number.isFinite(value) && value > 0));
    if (campaignTransactionParams.useStoredCampaigns && storedCampaignIdSet.size === 0) {
      const message = 'No stored campaigns are loaded for the active profile context. Run Campaign Directory Sync and verify the table before transactions.';
      setError(message);
      appendAutomationLog({ area: 'transactions-export', level: 'error', message: 'Blocked transactions export.', details: message });
      return;
    }
    if (parsedCampaignIds.length) {
      const missingCampaignIds = parsedCampaignIds.filter((campaignId) => !storedCampaignIdSet.has(campaignId));
      if (missingCampaignIds.length > 0) {
        const message = `Campaign ID(s) not found in the active profile's stored campaign table: ${missingCampaignIds.slice(0, 20).join(', ')}${missingCampaignIds.length > 20 ? '…' : ''}. Re-sync/verify campaigns before transactions.`;
        setError(message);
        appendAutomationLog({ area: 'transactions-export', level: 'error', message: 'Blocked transactions export because campaign IDs do not match active profile.', details: message });
        return;
      }
    }

    if (!campaignTransactionParams.startDate || !campaignTransactionParams.endDate) {
      setError('Start date and end date are required.');
      return;
    }

    const toOptionalInt = (value) => {
      const parsed = Number.parseInt(String(value || '').trim(), 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    };

    const params = {
      startDate: campaignTransactionParams.startDate,
      endDate: campaignTransactionParams.endDate,
      campaignIds: parsedCampaignIds,
      useStoredCampaigns: Boolean(campaignTransactionParams.useStoredCampaigns),
      ...(merchantIdIsValid ? { merchantId: String(credentials.merchantId).trim() } : {}),
      ...(toOptionalInt(campaignTransactionParams.affiliateId) ? { affiliateId: toOptionalInt(campaignTransactionParams.affiliateId) } : {}),
      ...(toOptionalInt(campaignTransactionParams.adId) ? { adId: toOptionalInt(campaignTransactionParams.adId) } : {}),
      ...(toOptionalInt(campaignTransactionParams.stepId) ? { stepId: toOptionalInt(campaignTransactionParams.stepId) } : {}),
      ...(campaignTransactionParams.isApproved ? { isApproved: true } : {}),
      ...(campaignTransactionParams.strictThrottle !== false ? { strictThrottle: true } : { strictThrottle: false }),
      ...(toOptionalInt(campaignTransactionParams.promoCode) ? { promoCode: toOptionalInt(campaignTransactionParams.promoCode) } : {}),
      ...(toOptionalInt(campaignTransactionParams.additionalIdType) ? { additionalIdType: toOptionalInt(campaignTransactionParams.additionalIdType) } : {}),
      ...(String(campaignTransactionParams.additionalIds || '').trim() ? { additionalIds: String(campaignTransactionParams.additionalIds).trim() } : {})
    };

    try {
      setSyncingCampaignTransactions(true);
      setError('');
      appendAutomationLog({ area: 'transactions-export', message: `Transactions export started for ${params.startDate} to ${params.endDate}.` });

      const effectiveCampaignIds = Array.isArray(params.campaignIds) && params.campaignIds.length
        ? params.campaignIds
        : (campaignTransactionParams.useStoredCampaigns ? Array.from(storedCampaignIdSet) : []);
      const chunkSize = Math.max(1, Math.min(200, Number.parseInt(String(campaignTransactionParams.chunkSize || '35'), 10) || 35));
      const interChunkDelayMs = Math.max(0, Math.min(30000, Number.parseInt(String(campaignTransactionParams.interChunkDelayMs || '1200'), 10) || 1200));
      const chunks = [];
      for (let i = 0; i < effectiveCampaignIds.length; i += chunkSize) {
        chunks.push(effectiveCampaignIds.slice(i, i + chunkSize));
      }

      const chunkResults = [];
      let processedCampaigns = 0;
      setCampaignTransactionChunkProgress({ totalCampaigns: effectiveCampaignIds.length, processedCampaigns: 0, totalChunks: chunks.length, completedChunks: 0 });
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex += 1) {
        const chunkCampaignIds = chunks[chunkIndex];
        appendAutomationLog({ area: 'transactions-export', message: `Running chunk ${chunkIndex + 1}/${chunks.length} (${chunkCampaignIds.length} campaigns).` });
        const response = await adminReportingAPI.syncLinkTrustTransactionsByCampaign({
          credentials: {
            username: credentials.username,
            password: credentials.password,
            ...(merchantIdIsValid ? { merchantId: String(credentials.merchantId).trim() } : {}),
            ...(String(credentials.transactionApiUrl || '').trim() ? { transactionApiUrl: String(credentials.transactionApiUrl).trim() } : {})
          },
          params: {
            ...params,
            campaignIds: chunkCampaignIds,
            useStoredCampaigns: false
          }
        });
        chunkResults.push(response?.data || null);
        processedCampaigns += chunkCampaignIds.length;
        setCampaignTransactionChunkProgress({
          totalCampaigns: effectiveCampaignIds.length,
          processedCampaigns,
          totalChunks: chunks.length,
          completedChunks: chunkIndex + 1
        });
        if (chunkIndex < chunks.length - 1 && interChunkDelayMs > 0) {
          await new Promise((resolve) => setTimeout(resolve, interChunkDelayMs));
        }
      }

      const result = mergeTransactionRunResults(chunkResults, {
        endpointBase: String(credentials.transactionApiUrl || '').trim(),
        authUsername: String(credentials.username || '').trim(),
        merchantId: merchantIdIsValid ? String(credentials.merchantId).trim() : null,
        strictThrottle: campaignTransactionParams.strictThrottle !== false,
        requestMinIntervalMs: campaignTransactionParams.strictThrottle !== false ? 2100 : 250,
        startDate: params.startDate,
        endDate: params.endDate
      });
      setCampaignTransactionResult(result);

      if ((result?.failedCount || 0) > 0) {
        setError(`Transactions run completed with ${result.failedCount} failed campaign call(s). Check failure details below.`);
        appendAutomationLog({ area: 'transactions-export', level: 'warn', message: 'Transactions export completed with failed campaign calls.', details: `Failed ${result.failedCount} call(s).` });
      }

      exportCampaignTransactionsResultFiles(result);
      appendAutomationLog({
        area: 'transactions-export',
        level: 'success',
        message: 'Transactions export finished.',
        details: `URL ${result?.endpointBase || credentials.transactionApiUrl || 'n/a'} · user ${result?.authUsername || credentials.username || 'n/a'} · account ${result?.merchantId || credentials.merchantId || 'none'} · throttle ${result?.strictThrottle === false ? 'off' : 'on'} (${result?.requestMinIntervalMs || 0}ms) · campaigns: ${result?.campaignCount || 0}, rows: ${result?.transactionCount || 0}, failed: ${result?.failedCount || 0}.`
      });
    } catch (requestError) {
      appendAutomationLog({ area: 'transactions-export', level: 'error', message: 'Campaign transactions export failed.', details: toDisplayError(requestError, 'Campaign transaction sync failed') });
      setError(toDisplayError(requestError, 'Campaign transaction sync failed'));
    } finally {
      setCampaignTransactionChunkProgress(null);
      setSyncingCampaignTransactions(false);
    }
  };

  const retryFailedCampaignTransactions = async () => {
    if (!requireAppliedProfileContext('transactions-export')) return;
    if (!hasAuthCredentials) {
      setError('Enter username and password first.');
      return;
    }

    const failedCampaignIds = Array.from(new Set(
      (campaignTransactionResult?.failedCampaigns || [])
        .map((item) => Number.parseInt(String(item?.campaignId || ''), 10))
        .filter((value) => Number.isFinite(value) && value > 0)
    ));

    if (!failedCampaignIds.length) {
      setError('No failed campaigns from the last transactions export run.');
      return;
    }

    if (!campaignTransactionParams.startDate || !campaignTransactionParams.endDate) {
      setError('Start date and end date are required.');
      return;
    }

    const toOptionalInt = (value) => {
      const parsed = Number.parseInt(String(value || '').trim(), 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    };

    const params = {
      startDate: campaignTransactionParams.startDate,
      endDate: campaignTransactionParams.endDate,
      campaignIds: failedCampaignIds,
      useStoredCampaigns: false,
      ...(merchantIdIsValid ? { merchantId: String(credentials.merchantId).trim() } : {}),
      ...(toOptionalInt(campaignTransactionParams.affiliateId) ? { affiliateId: toOptionalInt(campaignTransactionParams.affiliateId) } : {}),
      ...(toOptionalInt(campaignTransactionParams.adId) ? { adId: toOptionalInt(campaignTransactionParams.adId) } : {}),
      ...(toOptionalInt(campaignTransactionParams.stepId) ? { stepId: toOptionalInt(campaignTransactionParams.stepId) } : {}),
      ...(campaignTransactionParams.isApproved ? { isApproved: true } : {}),
      ...(campaignTransactionParams.strictThrottle !== false ? { strictThrottle: true } : { strictThrottle: false }),
      ...(toOptionalInt(campaignTransactionParams.promoCode) ? { promoCode: toOptionalInt(campaignTransactionParams.promoCode) } : {}),
      ...(toOptionalInt(campaignTransactionParams.additionalIdType) ? { additionalIdType: toOptionalInt(campaignTransactionParams.additionalIdType) } : {}),
      ...(String(campaignTransactionParams.additionalIds || '').trim() ? { additionalIds: String(campaignTransactionParams.additionalIds).trim() } : {})
    };

    try {
      setSyncingCampaignTransactions(true);
      setError('');
      appendAutomationLog({ area: 'transactions-export', message: `Retrying failed campaigns only (${failedCampaignIds.length}) for ${params.startDate} to ${params.endDate}.` });
      const response = await adminReportingAPI.syncLinkTrustTransactionsByCampaign({
        credentials: {
          username: credentials.username,
          password: credentials.password,
          ...(merchantIdIsValid ? { merchantId: String(credentials.merchantId).trim() } : {}),
          ...(String(credentials.transactionApiUrl || '').trim() ? { transactionApiUrl: String(credentials.transactionApiUrl).trim() } : {})
        },
        params
      });

      const result = response?.data || null;
      setCampaignTransactionResult(result);

      if ((result?.failedCount || 0) > 0) {
        setError(`Retry completed with ${result.failedCount} failed campaign call(s).`);
      }

      const endStamp = new Date().toISOString().slice(0, 10);
      const sourceRows = result?.transactions || [];
      const lt7ExactLike = ['lt7_transaction_details', 'lt7_transaction_details_excel_raw', 'lt7_transaction_details_csv', 'lt7_transaction_details_csv_raw'];
      const includeRawRemainder = ['lt7_transaction_details_excel_raw', 'lt7_transaction_details_csv_raw'].includes(campaignTransactionFormat);
      const summaryRows = summarizeTransactionRows(sourceRows);
      const exportRows = lt7ExactLike.includes(campaignTransactionFormat)
        ? formatLt7TransactionDetailsRows(sourceRows, { includeRawRemainder })
        : sourceRows;
      const formatSuffix = campaignTransactionFormat === 'lt7_transaction_details'
        ? 'lt7-transaction-details'
        : campaignTransactionFormat === 'lt7_transaction_details_excel_raw'
          ? 'lt7-transaction-details-excel-raw'
        : campaignTransactionFormat === 'lt7_transaction_details_csv'
          ? 'lt7-transaction-details-csv'
          : campaignTransactionFormat === 'lt7_transaction_details_csv_raw'
            ? 'lt7-transaction-details-csv-raw'
          : 'raw';

      if (campaignTransactionFormat === 'raw_and_summary_csv') {
        downloadCsv(
          `grab-all-campaign-transactions-retry-raw-${endStamp}.csv`,
          sourceRows,
          Object.keys(sourceRows[0] || { info: '' })
        );
        downloadCsv(
          `grab-all-campaign-transactions-retry-summary-${endStamp}.csv`,
          summaryRows,
          ['CID', 'AffiliateId', 'Status', 'Transactions', 'Revenue', 'Commission']
        );
      } else if (campaignTransactionFormat === 'lt7_transaction_details_csv' || campaignTransactionFormat === 'lt7_transaction_details_csv_raw') {
        downloadCsv(
          `grab-all-campaign-transactions-retry-${formatSuffix}-${endStamp}.csv`,
          exportRows,
          campaignTransactionFormat === 'lt7_transaction_details_csv_raw'
            ? buildLt7RawTailColumns(sourceRows)
            : LT7_TRANSACTION_DETAIL_COLUMNS
        );
      } else {
        downloadWorkbook(`grab-all-campaign-transactions-retry-${formatSuffix}-${endStamp}.xlsx`, exportRows);
      }

      appendAutomationLog({
        area: 'transactions-export',
        level: 'success',
        message: 'Retry failed campaigns export finished.',
        details: `Retried ${failedCampaignIds.length} campaign(s), rows: ${result?.transactionCount || 0}, failed: ${result?.failedCount || 0}.`
      });
    } catch (requestError) {
      appendAutomationLog({ area: 'transactions-export', level: 'error', message: 'Retry failed campaigns export failed.', details: toDisplayError(requestError, 'Retry failed campaigns export failed') });
      setError(toDisplayError(requestError, 'Retry failed campaigns export failed'));
    } finally {
      setSyncingCampaignTransactions(false);
    }
  };

  const toggleSelection = (value, stateSetter) => {
    stateSetter((current) => (current.includes(value) ? current.filter((item) => item !== value) : [...current, value]));
  };

  const toggleCampaignSelection = (campaignId) => {
    const id = Number.parseInt(campaignId, 10);
    if (!Number.isFinite(id) || id <= 0) {
      return;
    }

    setSelectedCampaignIds((current) => {
      const exists = current.includes(id);
      const nextCampaignIds = exists
        ? current.filter((item) => item !== id)
        : [...current, id];

      if (exists) {
        setCampaignAdSelections((currentMap) => {
          const nextMap = { ...currentMap };
          delete nextMap[String(id)];
          recomputeSelectedAdIds(nextMap);
          return nextMap;
        });
      }

      return nextCampaignIds;
    });
  };

  const recomputeSelectedAdIds = (nextMap) => {
    const allAdIds = Object.values(nextMap || {})
      .flatMap((ids) => (Array.isArray(ids) ? ids : []))
      .map((value) => Number.parseInt(value, 10))
      .filter((value) => Number.isFinite(value) && value > 0);
    setSelectedAdIds(Array.from(new Set(allAdIds)));
  };

  const fillCampaignIdsFromStored = (target = 'transactions') => {
    const ids = campaignOptions
      .map((campaign) => Number.parseInt(campaign.linktrust_campaign_id, 10))
      .filter((value) => Number.isFinite(value) && value > 0);
    const unique = Array.from(new Set(ids));
    const asText = unique.join(',');
    if (!unique.length) {
      setError('No stored campaigns found. Run Campaign Directory Sync first.');
      return;
    }

    if (target === 'click') {
      setClickListingParams((state) => ({
        ...state,
        campaignIdsText: asText,
        useStoredCampaigns: false
      }));
      return;
    }

    setCampaignTransactionParams((state) => ({
      ...state,
      campaignIdsText: asText,
      useStoredCampaigns: false
    }));
  };

  const toggleCampaignAdId = (campaignId, adId) => {
    const campaignKey = String(campaignId);
    const normalizedAdId = Number.parseInt(adId, 10);
    if (!Number.isFinite(normalizedAdId) || normalizedAdId <= 0) {
      return;
    }

    setCampaignAdSelections((current) => {
      const existing = Array.isArray(current[campaignKey]) ? current[campaignKey] : [];
      const nextCampaignAdIds = existing.includes(normalizedAdId)
        ? existing.filter((item) => item !== normalizedAdId)
        : [...existing, normalizedAdId];
      const next = {
        ...current,
        [campaignKey]: nextCampaignAdIds
      };
      recomputeSelectedAdIds(next);
      return next;
    });
  };

  const addManualCampaignAdIds = (campaignId) => {
    const campaignKey = String(campaignId);
    const parsed = parseNumericIdList(campaignAdDrafts[campaignKey] || '');
    if (!parsed.length) {
      return;
    }

    setCampaignAdSelections((current) => {
      const existing = Array.isArray(current[campaignKey]) ? current[campaignKey] : [];
      const next = {
        ...current,
        [campaignKey]: Array.from(new Set([...existing, ...parsed]))
      };
      recomputeSelectedAdIds(next);
      return next;
    });

    setCampaignAdDrafts((current) => ({ ...current, [campaignKey]: '' }));
  };

  const loadAdIdsFromSelectedCampaigns = () => {
    const next = {};
    selectedCampaignIds.forEach((campaignId) => {
      const options = creativeOptionsByCampaign[String(campaignId)] || creativeOptionsByCampaign[Number(campaignId)] || [];
      next[String(campaignId)] = options.map((item) => item.adId);
    });
    setCampaignAdSelections(next);
    recomputeSelectedAdIds(next);
  };

  const selectFilteredCampaigns = () => {
    const ids = filteredCampaignOptions
      .map((campaign) => Number.parseInt(campaign.linktrust_campaign_id, 10))
      .filter((value) => Number.isFinite(value) && value > 0);
    setSelectedCampaignIds((current) => Array.from(new Set([...current, ...ids])));
  };

  const clearFilteredCampaigns = () => {
    const filteredIds = new Set(
      filteredCampaignOptions
        .map((campaign) => Number.parseInt(campaign.linktrust_campaign_id, 10))
        .filter((value) => Number.isFinite(value) && value > 0)
    );
    setSelectedCampaignIds((current) => current.filter((value) => !filteredIds.has(value)));
    setCampaignAdSelections((currentMap) => {
      const nextMap = { ...currentMap };
      filteredIds.forEach((id) => {
        delete nextMap[String(id)];
      });
      recomputeSelectedAdIds(nextMap);
      return nextMap;
    });
  };

  const syncCreativesForSelectedCampaigns = async () => {
    if (!selectedCampaignIds.length) {
      setError('Select at least one campaign first.');
      return;
    }
    await syncCreatives({ campaignIds: selectedCampaignIds });
  };

  const generateLinks = async (previewOnly = true) => {
    if (!selectedCampaignIds.length || !selectedAffiliateIds.length) {
      setError('Pick at least one campaign and one affiliate.');
      return;
    }

    const selectedMerchantIds = Array.from(new Set(
      selectedCampaignIds
        .map((campaignId) => campaignOptions.find((item) => Number(item.linktrust_campaign_id) === Number(campaignId))?.merchant_id)
        .map((value) => Number.parseInt(value, 10))
        .filter((value) => Number.isFinite(value) && value > 0)
    ));
    const merchantForLinks = merchantIdIsValid
      ? Number.parseInt(credentials.merchantId, 10)
      : (selectedMerchantIds.length === 1 ? selectedMerchantIds[0] : null);
    if (!merchantForLinks) {
      setError('Select campaigns from a single merchant or set Merchant ID context before generating links.');
      return;
    }

    try {
      setGeneratingLinks(true);
      setError('');
      const response = await adminReportingAPI.generateLinkTrustLinks({
        merchantId: merchantForLinks,
        campaignIds: selectedCampaignIds,
        affiliateIds: selectedAffiliateIds,
        adIds: selectedAdIds,
        options: {
          includeAdIds,
          includeBaseLinks,
          previewOnly,
          baseUrl,
          campaignAdMap: campaignAdSelections
        }
      });
      setLinksResult(response?.data || null);
      if (!previewOnly) {
        await refreshAll();
      }
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Link generation failed'));
    } finally {
      setGeneratingLinks(false);
    }
  };

  const openMappingDrawer = (type) => {
    setMappingRowIndex(0);
    setMappingDrawer({ open: true, type });
  };

  const saveMappingProfile = async (profileType) => {
    const merchantId = merchantIdIsValid
      ? String(credentials.merchantId).trim()
      : (selectedAdvertiserScopeOption ? String(selectedAdvertiserScopeOption.merchantId) : undefined);
    const isCampaign = profileType === 'campaign';
    const profileName = (isCampaign ? campaignProfileName : creativeProfileName).trim();
    if (!profileName) {
      setError('Enter a profile name before saving mapping profile.');
      return;
    }

    const headerOverrides = isCampaign ? campaignFieldOverrides : creativeFieldOverrides;
    const metadata = isCampaign
      ? { categoryOverride: campaignCategoryOverride || null }
      : { campaignIdOverride: creativeCampaignOverride || null };

    try {
      setError('');
      await adminReportingAPI.saveLinkTrustMappingProfile({
        profileType,
        profileName,
        merchantId,
        sourceKey: selectedScopeKey || undefined,
        headerOverrides,
        metadata
      });
      await loadMappingProfiles(String(credentials.merchantId || '').trim());
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Could not save mapping profile'));
    }
  };

  const applyMappingProfile = (profileType) => {
    const isCampaign = profileType === 'campaign';
    const profileId = isCampaign ? selectedCampaignProfileId : selectedCreativeProfileId;
    const profiles = isCampaign ? scopedCampaignProfiles : scopedCreativeProfiles;
    const profile = profiles.find((item) => String(item.id) === String(profileId));
    if (!profile) {
      setError('Select a saved mapping profile first.');
      return;
    }

    if (isCampaign) {
      setCampaignFieldOverrides(profile.header_overrides || {});
      if (profile?.metadata?.categoryOverride) {
        setCampaignCategoryOverride(String(profile.metadata.categoryOverride));
      }
    } else {
      setCreativeFieldOverrides(profile.header_overrides || {});
      if (profile?.metadata?.campaignIdOverride) {
        setCreativeCampaignOverride(String(profile.metadata.campaignIdOverride));
      }
    }
  };

  const deleteMappingProfile = async (profileType) => {
    const isCampaign = profileType === 'campaign';
    const profileId = isCampaign ? selectedCampaignProfileId : selectedCreativeProfileId;
    if (!profileId) {
      setError('Select a saved mapping profile first.');
      return;
    }

    try {
      setError('');
      await adminReportingAPI.deleteLinkTrustMappingProfile(profileId);
      if (isCampaign) setSelectedCampaignProfileId('');
      else setSelectedCreativeProfileId('');
      await loadMappingProfiles(String(credentials.merchantId || '').trim());
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Could not delete mapping profile'));
    }
  };

  const saveAutomationProfile = async () => {
    if (!automationClientName.trim() || !automationProfileName.trim()) {
      setError('Client name and profile name are required to save automation profile.');
      return;
    }

    try {
      setSavingAutomationProfile(true);
      setError('');
      const saved = await adminReportingAPI.saveLinkTrustAutomationProfile({
        clientName: automationClientName.trim(),
        profileName: automationProfileName.trim(),
        ...(merchantIdIsValid ? { merchantId: Number.parseInt(String(credentials.merchantId).trim(), 10) } : {}),
        credentials: {
          username: credentials.username,
          password: credentials.password,
          merchantId: credentials.merchantId,
          apiUrl: credentials.apiUrl,
          transactionApiUrl: credentials.transactionApiUrl,
          clickListingApiUrl: LOCKED_CLICK_LISTING_API_URL
        },
        payload: {
          activeWorkflow,
          campaignTransactionParams,
          campaignTransactionFormat,
          clickListingParams,
          clickListingFormat,
          syncContext: {
            merchantId: normalizeMerchantId(credentials.merchantId),
            requireAppliedProfileForSync: true
          }
        },
        metadata: {
          selectedCampaignIds,
          savedAt: new Date().toISOString()
        }
      });
      const savedProfileId = saved?.data?.profile?.id;
      if (savedProfileId) {
        setSelectedAutomationProfileId(String(savedProfileId));
        const response = await adminReportingAPI.getLinkTrustAutomationProfile(savedProfileId);
        const profile = response?.data?.profile;
        if (profile) persistAutomationProfileContext(profile);
      }
      appendAutomationLog({ area: 'profile', level: 'success', message: 'Automation profile saved and applied.', details: `${automationClientName.trim()} · ${automationProfileName.trim()}` });
      await loadAutomationProfiles(String(credentials.merchantId || '').trim());
    } catch (requestError) {
      appendAutomationLog({ area: 'profile', level: 'error', message: 'Failed to save automation profile.', details: toDisplayError(requestError, 'Failed to save automation profile') });
      setError(toDisplayError(requestError, 'Failed to save automation profile'));
    } finally {
      setSavingAutomationProfile(false);
    }
  };

  const applyAutomationProfile = async () => {
    if (!selectedAutomationProfileId) {
      setError('Select an automation profile first.');
      return;
    }
    try {
      setError('');
      const response = await adminReportingAPI.getLinkTrustAutomationProfile(selectedAutomationProfileId);
      const profile = response?.data?.profile;
      if (!profile) {
        setError('Selected automation profile not found.');
        return;
      }

      const creds = profile.credentials || {};
      setCredentials((state) => ({
        ...state,
        username: creds.username || state.username,
        password: creds.password || state.password,
        merchantId: normalizeMerchantId(creds.merchantId || state.merchantId),
        apiUrl: creds.apiUrl || state.apiUrl,
        transactionApiUrl: creds.transactionApiUrl || state.transactionApiUrl,
        clickListingApiUrl: LOCKED_CLICK_LISTING_API_URL
      }));

      const payload = profile.profile_payload || {};
      if (payload.activeWorkflow) setActiveWorkflow(payload.activeWorkflow);
      if (payload.campaignTransactionParams) setCampaignTransactionParams((state) => ({ ...state, ...payload.campaignTransactionParams }));
      if (payload.campaignTransactionFormat) setCampaignTransactionFormat(payload.campaignTransactionFormat);
      if (payload.clickListingParams) setClickListingParams((state) => ({ ...state, ...payload.clickListingParams }));
      if (payload.clickListingFormat) setClickListingFormat(payload.clickListingFormat);

      const selected = profile.metadata?.selectedCampaignIds;
      if (Array.isArray(selected)) {
        setSelectedCampaignIds(selected.map((v) => Number.parseInt(v, 10)).filter((v) => Number.isFinite(v) && v > 0));
      }
      setAutomationClientName(profile.client_name || '');
      setAutomationProfileName(profile.profile_name || '');
      persistAutomationProfileContext(profile);
      appendAutomationLog({ area: 'profile', level: 'success', message: 'Automation profile applied.', details: `${profile.client_name || 'Client'} · ${profile.profile_name || `Profile ${profile.id}`}` });
    } catch (requestError) {
      appendAutomationLog({ area: 'profile', level: 'error', message: 'Failed to apply automation profile.', details: toDisplayError(requestError, 'Failed to load automation profile') });
      setError(toDisplayError(requestError, 'Failed to load automation profile'));
    }
  };

  const deleteAutomationProfile = async () => {
    if (!selectedAutomationProfileId) {
      setError('Select an automation profile first.');
      return;
    }
    try {
      setError('');
      await adminReportingAPI.deleteLinkTrustAutomationProfile(selectedAutomationProfileId);
      if (appliedAutomationProfile?.profileId && String(appliedAutomationProfile.profileId) === String(selectedAutomationProfileId)) {
        sessionStorage.removeItem(ACTIVE_AUTOMATION_PROFILE_CONTEXT_KEY);
        setAppliedAutomationProfile(null);
      }
      setSelectedAutomationProfileId('');
      await loadAutomationProfiles(String(credentials.merchantId || '').trim());
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Failed to delete automation profile'));
    }
  };

  const SyncCard = ({ title, children, className = '' }) => (
    <div className={`admin-glass rounded-xl border border-slate-700/50 p-4 space-y-3 ${className}`.trim()}>
      <h3 className="text-slate-100 font-semibold inline-flex items-center"><Database className="w-4 h-4 mr-2 text-red-300" />{title}</h3>
      {children}
    </div>
  );

  const directorySyncContent = (
    <>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <SyncCard title="Advertiser Directory Sync">
          <button onClick={syncAdvertisers} disabled={!hasAuthCredentials || syncingAdvertisers} className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white rounded-lg">{syncingAdvertisers ? 'Syncing...' : 'Sync Advertiser Listing'}</button>
          <p className="text-xs text-slate-400">{advertiserSyncResult ? `Stored ${advertiserSyncResult.storedCount || 0} advertiser record(s) for merchant IDs: ${(advertiserSyncResult.merchantIds || []).join(', ') || 'N/A'}` : 'No advertiser sync yet.'}</p>
          <p className="text-xs text-slate-500">Stored advertiser records: {storedAdvertisers.length}</p>
        </SyncCard>

        <SyncCard title="Campaign Directory Sync">
          <div className="flex flex-wrap gap-2">
            <button onClick={syncCampaigns} disabled={!hasAuthCredentials || syncingCampaigns || savingCampaignPreview} className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white rounded-lg">{syncingCampaigns ? 'Fetching...' : 'Fetch Campaign Listing'}</button>
            <button onClick={confirmSaveCampaignSyncPreview} disabled={!hasAuthCredentials || savingCampaignPreview || !(campaignSyncPreview?.previewRows || []).length} className="px-4 py-2 bg-emerald-800 hover:bg-emerald-700 disabled:opacity-60 text-emerald-50 rounded-lg">{savingCampaignPreview ? 'Saving...' : 'Confirm Save Campaigns'}</button>
          </div>
          <p className="text-xs text-slate-400">{campaignSyncPreview ? `Fetched ${campaignSyncPreview.saveableCount || 0} campaign record(s) for review. Confirm to save.` : campaignSyncResult ? `Stored ${campaignSyncResult.storedCount || 0} campaign record(s) for merchant IDs: ${(campaignSyncResult.merchantIds || []).join(', ') || 'N/A'}` : 'No campaign fetch yet.'}</p>
          <p className="text-xs text-slate-500">Stored campaign records: {campaignOptions.length}</p>
        </SyncCard>

        <SyncCard title="Creative Directory Sync">
          <button onClick={syncCreativesForSelectedCampaigns} disabled={!hasAuthCredentials || syncingCreatives || !selectedCampaignIds.length} className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white rounded-lg">{syncingCreatives ? 'Syncing...' : 'Sync for Selected Campaigns'}</button>
          <button onClick={() => syncCreatives({})} disabled={!hasAuthCredentials || syncingCreatives} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-slate-100 rounded-lg">{syncingCreatives ? 'Syncing...' : 'Sync All Creatives'}</button>
          <p className="text-xs text-slate-400">{creativeSyncResult ? `Stored ${creativeSyncResult.storedCount || 0} creative record(s) from ${creativeSyncResult.fetchedRows || 0} fetched row(s).` : 'No creative sync yet.'}</p>
          <p className="text-xs text-slate-500">Selected campaigns for scoped sync: {selectedCampaignIds.length}</p>
        </SyncCard>

        <SyncCard title="Affiliate Directory Sync">
          <button onClick={syncAffiliates} disabled={!hasAuthCredentials || syncingAffiliates} className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white rounded-lg">{syncingAffiliates ? 'Syncing...' : 'Sync Affiliates'}</button>
          <p className="text-xs text-slate-400">{affiliateSyncResult ? `Stored ${affiliateSyncResult.storedCount || affiliateSyncResult.affiliatesCount || 0} record(s) for merchant IDs: ${(affiliateSyncResult.merchantIds || []).join(', ') || 'N/A'}` : 'No affiliate sync yet.'}</p>
          <p className="text-xs text-slate-500">Stored affiliate records: {storedAffiliates.length}</p>
        </SyncCard>

        <SyncCard title="Affiliate Lead Status Sync">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input type="date" value={leadParams.startDate} onChange={(e) => setLeadParams((s) => ({ ...s, startDate: e.target.value }))} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100" />
            <input type="date" value={leadParams.endDate} onChange={(e) => setLeadParams((s) => ({ ...s, endDate: e.target.value }))} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100" />
            <select value={leadParams.trafficFilterLeadStats} onChange={(e) => setLeadParams((s) => ({ ...s, trafficFilterLeadStats: e.target.value }))} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100"><option value="1">All</option><option value="2">InvalidDataField</option><option value="3">CampaignRules</option><option value="4">ThirdPartyRules</option><option value="5">TierRules</option><option value="6">ChannelRules</option><option value="7">RejectedResponse</option></select>
          </div>
          <button onClick={syncLeadStatus} disabled={!hasMerchantCredentials || syncingLeadStatus} className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white rounded-lg">{syncingLeadStatus ? 'Syncing...' : 'Sync Lead Status'}</button>
          <p className="text-xs text-slate-400">{leadSyncResult ? `Synced ${leadSyncResult.startDate} to ${leadSyncResult.endDate} (filter ${leadSyncResult.trafficFilterLeadStats})` : 'No lead status sync yet.'}</p>
        </SyncCard>
      </div>

      {campaignSyncPreview && (
        <details open className="admin-glass rounded-xl border border-amber-700/40 overflow-hidden">
          <summary className="cursor-pointer p-4 border-b border-slate-700/50 text-slate-100 font-semibold">
            Review fetched campaigns before saving · {campaignSyncPreview.saveableCount || 0} saveable · {campaignSyncPreview.skippedCount || 0} skipped
          </summary>
          <div className="p-4 space-y-3">
            <div className="rounded-md border border-slate-700 bg-slate-900/40 p-3 text-xs text-slate-300 space-y-1">
              <p>Endpoint: <span className="text-slate-100">{campaignSyncPreview.endpoint || 'n/a'}</span></p>
              <p>Username: <span className="text-slate-100">{campaignSyncPreview.authUsername || 'n/a'}</span> · Expected account/advertiser: <span className="text-slate-100">{campaignSyncPreview.merchantIdSeed || 'none'}</span></p>
              <p>Returned account/advertiser IDs: <span className="text-slate-100">{(campaignSyncPreview.merchantIds || []).join(', ') || 'none'}</span></p>
              {(campaignSyncPreview.skippedSamples || []).length > 0 && (
                <p className="text-amber-300">Skipped sample: {JSON.stringify(campaignSyncPreview.skippedSamples[0])}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={confirmSaveCampaignSyncPreview} disabled={savingCampaignPreview || !(campaignSyncPreview?.previewRows || []).length} className="px-4 py-2 bg-emerald-800 hover:bg-emerald-700 disabled:opacity-60 text-emerald-50 rounded-lg">{savingCampaignPreview ? 'Saving...' : 'Confirm Save This Fetched Set'}</button>
              <button type="button" onClick={() => setCampaignSyncPreview(null)} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg">Discard Preview</button>
            </div>
            <div className="max-h-96 overflow-auto rounded-lg border border-slate-700">
              <table className="w-full text-xs">
                <thead className="bg-slate-900/90 text-slate-400 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2">Campaign ID</th>
                    <th className="text-left px-4 py-2">Campaign Name</th>
                    <th className="text-left px-4 py-2">Advertiser</th>
                    <th className="text-left px-4 py-2">Account/Advertiser ID</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-left px-4 py-2">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {(campaignSyncPreview.previewRows || []).slice(0, 1000).map((campaign, index) => (
                    <tr key={`campaign-preview-${campaign.campaignId || index}`} className="border-t border-slate-700/60 text-slate-200">
                      <td className="px-4 py-2">{campaign.campaignId || '-'}</td>
                      <td className="px-4 py-2">{campaign.campaignName || '-'}</td>
                      <td className="px-4 py-2">{campaign.advertiserName || '-'}</td>
                      <td className="px-4 py-2">{campaign.merchantId || '-'}</td>
                      <td className="px-4 py-2">{campaign.campaignStatus || '-'}</td>
                      <td className="px-4 py-2">{campaign.category || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </details>
      )}

      <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
          <h3 className="text-slate-100 font-semibold">Stored Advertisers</h3>
          <p className="text-xs text-slate-500">{merchantIdIsValid ? `Merchant ${credentials.merchantId}` : 'All merchants'} · {storedAdvertisers.length} records</p>
        </div>
        <div className="max-h-72 overflow-auto">
          {storedAdvertisers.length === 0 ? (
            <p className="p-4 text-sm text-slate-400">No advertisers synced yet.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-900/50 text-slate-400">
                <tr>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Company</th>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2">Location</th>
                  <th className="text-left px-4 py-2">Updated</th>
                </tr>
              </thead>
              <tbody>
                {storedAdvertisers.map((advertiser, index) => (
                  <tr key={`advertiser-row-${index}`} className="border-t border-slate-700/60 text-slate-200">
                    <td className="px-4 py-2">{advertiser.advertiser_name || '-'}</td>
                    <td className="px-4 py-2">{advertiser.company_name || '-'}</td>
                    <td className="px-4 py-2">{advertiser.email || '-'}</td>
                    <td className="px-4 py-2">{[advertiser.city, advertiser.state, advertiser.country].filter(Boolean).join(', ') || '-'}</td>
                    <td className="px-4 py-2">{advertiser.updated_at ? new Date(advertiser.updated_at).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
          <h3 className="text-slate-100 font-semibold">Stored Campaigns (verification)</h3>
          <p className="text-xs text-slate-500">{merchantIdIsValid ? `Merchant ${credentials.merchantId}` : 'All merchants'} · {campaignOptions.length} records</p>
        </div>
        <div className="max-h-80 overflow-auto">
          {campaignOptions.length === 0 ? (
            <p className="p-4 text-sm text-slate-400">No campaigns synced yet.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-900/50 text-slate-400 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2">Campaign ID</th>
                  <th className="text-left px-4 py-2">Campaign Name</th>
                  <th className="text-left px-4 py-2">Advertiser</th>
                  <th className="text-left px-4 py-2">Account/Advertiser ID</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Updated</th>
                </tr>
              </thead>
              <tbody>
                {campaignOptions.slice(0, 500).map((campaign, index) => (
                  <tr key={`stored-campaign-${campaign.linktrust_campaign_id || index}`} className="border-t border-slate-700/60 text-slate-200">
                    <td className="px-4 py-2">{campaign.linktrust_campaign_id || '-'}</td>
                    <td className="px-4 py-2">{campaign.campaign_name || '-'}</td>
                    <td className="px-4 py-2">{campaign.advertiser_name || '-'}</td>
                    <td className="px-4 py-2">{campaign.merchant_id || '-'}</td>
                    <td className="px-4 py-2">{campaign.campaign_status || '-'}</td>
                    <td className="px-4 py-2">{campaign.updated_at ? new Date(campaign.updated_at).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {campaignOptions.length > 500 && (
          <p className="px-4 py-2 text-[11px] text-slate-500 border-t border-slate-700/50">Showing first 500 campaigns. Use Management for full list and operations.</p>
        )}
      </div>
    </>
  );

  const exportJobsContent = (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <SyncCard title="Export click listing (summary + raw)">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <input type="date" value={clickListingParams.startDate} onChange={(e) => setClickListingParams((s) => ({ ...s, startDate: e.target.value }))} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100" />
        <input type="date" value={clickListingParams.endDate} onChange={(e) => setClickListingParams((s) => ({ ...s, endDate: e.target.value }))} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100" />
      </div>
      <input
        placeholder="Campaign IDs (optional, comma/space separated). Leave blank for all campaigns"
        value={clickListingParams.campaignIdsText}
        onChange={(e) => setClickListingParams((s) => ({ ...s, campaignIdsText: e.target.value }))}
        className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100"
      />
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => fillCampaignIdsFromStored('click')} className="px-2 py-1 bg-slate-800 hover:bg-slate-700 rounded text-xs text-slate-100">Use all stored campaign IDs</button>
        <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={clickListingParams.useStoredCampaigns} onChange={(e) => setClickListingParams((s) => ({ ...s, useStoredCampaigns: e.target.checked }))} className="rounded bg-slate-700 border-slate-600" />Auto-load stored campaigns in backend</label>
      </div>
      <input
        placeholder="AffiliateId (optional)"
        value={clickListingParams.affiliateId}
        onChange={(e) => setClickListingParams((s) => ({ ...s, affiliateId: normalizeMerchantId(e.target.value) }))}
        className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100"
      />
      <input
        placeholder="Click listing endpoint URL"
        value={LOCKED_CLICK_LISTING_API_URL}
        readOnly
        className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <label className="inline-flex items-center gap-2 text-xs text-slate-300">
          <input
            type="checkbox"
            checked={clickListingParams.autoResumeEnabled !== false}
            onChange={(e) => setClickListingParams((s) => ({ ...s, autoResumeEnabled: e.target.checked }))}
            className="rounded bg-slate-700 border-slate-600"
          />
          Auto-resume stalled jobs
        </label>
        <input
          type="number"
          min="120"
          step="60"
          placeholder="Stall threshold seconds"
          value={clickListingParams.autoResumeStallThresholdSeconds}
          onChange={(e) => setClickListingParams((s) => ({ ...s, autoResumeStallThresholdSeconds: e.target.value.replace(/[^0-9]/g, '') }))}
          className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100"
        />
      </div>
      <select
        value={clickListingFormat}
        onChange={(e) => setClickListingFormat(e.target.value)}
        className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100"
      >
        <option value="both_csv">Output: Summary + Raw CSV files</option>
        <option value="summary_csv">Output: Summary CSV file only</option>
        <option value="raw_csv">Output: Raw CSV file(s) only</option>
      </select>
      <button onClick={syncClickListing} disabled={!hasAuthCredentials || syncingClickListing} className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white rounded-lg">{syncingClickListing ? 'Starting...' : 'Start Background Click Export'}</button>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => controlClickListingJob('pause')}
          disabled={!clickListingJobRun || clickListingJobRun.status !== 'running'}
          className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-slate-100 rounded text-xs"
        >Pause</button>
        <button
          type="button"
          onClick={() => controlClickListingJob('resume')}
          disabled={!clickListingJobRun || clickListingJobRun.status !== 'running'}
          className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-slate-100 rounded text-xs"
        >Resume</button>
        <button
          type="button"
          onClick={() => controlClickListingJob('cancel')}
          disabled={!clickListingJobRun || clickListingJobRun.status !== 'running'}
          className="px-3 py-1.5 bg-rose-800 hover:bg-rose-700 disabled:opacity-60 text-rose-100 rounded text-xs"
        >Cancel</button>
        <button
          type="button"
          onClick={retryFailedClickListingJob}
          disabled={!clickListingJobRun || !['failed', 'completed'].includes(clickListingJobRun.status)}
          className="px-3 py-1.5 bg-amber-800 hover:bg-amber-700 disabled:opacity-60 text-amber-100 rounded text-xs"
        >Retry failed scopes</button>
        <button
          type="button"
          onClick={resumeClickListingJobFromCheckpoint}
          disabled={!clickListingJobRun || !['running', 'failed'].includes(clickListingJobRun.status)}
          className="px-3 py-1.5 bg-blue-800 hover:bg-blue-700 disabled:opacity-60 text-blue-100 rounded text-xs"
        >Resume from checkpoint</button>
      </div>
      <p className="text-xs text-slate-400">{clickListingJobRun ? `Job #${clickListingJobRun.id} · ${clickListingJobRun.status}` : 'No click listing job started yet.'}</p>
      {clickListingJobRun?.run_context && (
        <div className="rounded-md border border-slate-700 bg-slate-900/40 p-2 text-xs text-slate-300 space-y-1">
          <p>Progress: {clickListingJobRun.run_context.progressPercent ?? 0}% · Clicks: {clickListingJobRun.run_context.clickCount || 0} · Pages: {clickListingJobRun.run_context.pagesProcessed || 0}</p>
          <p>Scopes: {clickListingJobRun.run_context.scopeCompleted || 0}/{clickListingJobRun.run_context.scopeTotal || 0} · Failed calls: {clickListingJobRun.run_context.failedCount || 0}</p>
          <p>ETA: {clickListingJobRun.run_context.etaSeconds != null ? `${clickListingJobRun.run_context.etaSeconds}s` : 'calculating...'}</p>
          {clickListingJobRun.run_context.message && (
            <p className="text-rose-300">Failure: {clickListingJobRun.run_context.message}</p>
          )}
          {Array.isArray(clickListingJobRun.run_context.failures) && clickListingJobRun.run_context.failures.length > 0 && (
            <p className="text-amber-300">
              First failed scope: {clickListingJobRun.run_context.failures[0]?.startDate || 'n/a'} to {clickListingJobRun.run_context.failures[0]?.endDate || 'n/a'} ·
              status {clickListingJobRun.run_context.failures[0]?.statusCode || 'n/a'} ·
              {clickListingJobRun.run_context.failures[0]?.error || 'Unknown error'}
            </p>
          )}
          <p>
            Heartbeat:{' '}
            {clickListingJobRun.run_context.lastHeartbeatAt
              ? new Date(clickListingJobRun.run_context.lastHeartbeatAt).toLocaleString()
              : 'n/a'}
          </p>
          {clickListingJobRun.status === 'running' && clickListingJobRun.run_context.lastHeartbeatAt && ((Date.now() - new Date(clickListingJobRun.run_context.lastHeartbeatAt).getTime()) > 120000) && (
            <p className="text-amber-300">Possible stall detected (no heartbeat for 2+ minutes). Use “Resume from checkpoint”.</p>
          )}
          {clickListingJobRun.run_context.autoResume?.spawnedRunId && (
            <p className="text-blue-300">
              Auto-resume spawned run #{clickListingJobRun.run_context.autoResume.spawnedRunId}
            </p>
          )}
          {clickListingJobRun.run_context.downloads?.summary?.url && (
            <p>
              Summary file:{' '}
              <a
                href={buildApiDownloadUrl(clickListingJobRun.run_context.downloads.summary.url)}
                target="_blank"
                rel="noreferrer"
                className="text-blue-300 hover:text-blue-200 underline"
              >
                {clickListingJobRun.run_context.downloads.summary.name || 'download'}
              </a>
              {clickListingJobRun.run_context.downloads.summary.gzipUrl && (
                <>
                  {' '}·{' '}
                  <a
                    href={buildApiDownloadUrl(clickListingJobRun.run_context.downloads.summary.gzipUrl)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-300 hover:text-blue-200 underline"
                  >
                    {clickListingJobRun.run_context.downloads.summary.gzipName || 'download .gz'}
                  </a>
                </>
              )}
            </p>
          )}
          {clickListingJobRun.run_context.downloads?.rawCombined?.url && (
            <p>
              Combined raw file:{' '}
              <a
                href={buildApiDownloadUrl(clickListingJobRun.run_context.downloads.rawCombined.url)}
                target="_blank"
                rel="noreferrer"
                className="text-blue-300 hover:text-blue-200 underline"
              >
                {clickListingJobRun.run_context.downloads.rawCombined.name || 'download'}
              </a>
              {clickListingJobRun.run_context.downloads.rawCombined.gzipUrl && (
                <>
                  {' '}·{' '}
                  <a
                    href={buildApiDownloadUrl(clickListingJobRun.run_context.downloads.rawCombined.gzipUrl)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-300 hover:text-blue-200 underline"
                  >
                    {clickListingJobRun.run_context.downloads.rawCombined.gzipName || 'download .gz'}
                  </a>
                </>
              )}
            </p>
          )}
          {Array.isArray(clickListingJobRun.run_context.downloads?.rawMonthly) && clickListingJobRun.run_context.downloads.rawMonthly.length > 0 && (
            <div className="space-y-1">
              <p>Raw files:</p>
              <div className="max-h-24 overflow-auto space-y-1">
                {clickListingJobRun.run_context.downloads.rawMonthly.map((file) => (
                  <p key={`click-raw-file-${file.name}`}>
                    <a
                      href={buildApiDownloadUrl(file.url)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-300 hover:text-blue-200 underline"
                    >
                      {file.name}
                    </a>
                    {file.gzipUrl && (
                      <>
                        {' '}·{' '}
                        <a
                          href={buildApiDownloadUrl(file.gzipUrl)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-300 hover:text-blue-200 underline"
                        >
                          {file.gzipName || '.gz'}
                        </a>
                      </>
                    )}
                    <span className="text-slate-500"> · {file.rows || 0} rows</span>
                  </p>
                ))}
              </div>
            </div>
          )}
          {Array.isArray(clickListingJobRun.run_context.logs) && clickListingJobRun.run_context.logs.length > 0 && (
            <div className="mt-2 space-y-1">
              <p>Latest logs:</p>
              <div className="max-h-28 overflow-auto rounded border border-slate-700 bg-slate-950/40 p-2 space-y-1">
                {clickListingJobRun.run_context.logs.slice(-30).reverse().map((entry, idx) => (
                  <p key={`click-log-${idx}`} className={entry.level === 'error' ? 'text-rose-300' : entry.level === 'warn' ? 'text-amber-300' : 'text-slate-300'}>
                    [{entry.ts ? new Date(entry.ts).toLocaleTimeString() : 'n/a'}] {String(entry.level || 'info').toUpperCase()} · {entry.message}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="mt-2 rounded-md border border-slate-700 bg-slate-900/40 p-2">
        <p className="text-xs text-slate-300 mb-2">Recent click export jobs</p>
        <div className="max-h-48 overflow-auto">
          <table className="w-full text-[11px]">
            <thead className="text-slate-400 sticky top-0 bg-slate-900/90">
              <tr>
                <th className="text-left px-2 py-1">Run</th>
                <th className="text-left px-2 py-1">Status</th>
                <th className="text-left px-2 py-1">Progress</th>
                <th className="text-left px-2 py-1">Clicks</th>
                <th className="text-left px-2 py-1">Pages</th>
                <th className="text-left px-2 py-1">Failed</th>
                <th className="text-left px-2 py-1">ETA</th>
              </tr>
            </thead>
            <tbody>
              {clickListingJobs.map((run) => {
                const ctx = run.run_context || {};
                const heartbeatAgeMs = ctx.lastHeartbeatAt ? (Date.now() - new Date(ctx.lastHeartbeatAt).getTime()) : null;
                const isPossiblyStalled = run.status === 'running' && heartbeatAgeMs != null && heartbeatAgeMs > 120000;
                return (
                  <tr key={`click-job-${run.id}`} className="border-t border-slate-700/60 text-slate-200">
                    <td className="px-2 py-1">
                      <button type="button" onClick={() => { setClickListingJobRunId(run.id); setClickListingJobRun(run); }} className="text-blue-300 hover:text-blue-200 underline">#{run.id}</button>
                    </td>
                    <td className="px-2 py-1">{run.status}{isPossiblyStalled ? ' (stalled?)' : ''}</td>
                    <td className="px-2 py-1">{ctx.progressPercent ?? 0}%</td>
                    <td className="px-2 py-1">{ctx.clickCount || 0}</td>
                    <td className="px-2 py-1">{ctx.pagesProcessed || 0}</td>
                    <td className="px-2 py-1">{ctx.failedCount || 0}</td>
                    <td className="px-2 py-1">{ctx.etaSeconds != null ? `${ctx.etaSeconds}s` : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-[11px] text-slate-500">Runs in background with monthly splitting, progress polling, and downloadable output files.</p>
      <p className="text-[11px] text-slate-500">Auto-resume checks heartbeat age and spawns a checkpoint resume run when a stall threshold is exceeded.</p>
    </SyncCard>

      <SyncCard title="Grab all campaign transactions">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <input type="date" value={campaignTransactionParams.startDate} onChange={(e) => setCampaignTransactionParams((s) => ({ ...s, startDate: e.target.value }))} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100" />
        <input type="date" value={campaignTransactionParams.endDate} onChange={(e) => setCampaignTransactionParams((s) => ({ ...s, endDate: e.target.value }))} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100" />
      </div>
      <input
        placeholder="Campaign IDs (comma/space separated). Leave blank to use stored campaigns"
        value={campaignTransactionParams.campaignIdsText}
        onChange={(e) => setCampaignTransactionParams((s) => ({ ...s, campaignIdsText: e.target.value }))}
        className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100"
      />
      <button type="button" onClick={() => fillCampaignIdsFromStored('transactions')} className="px-2 py-1 bg-slate-800 hover:bg-slate-700 rounded text-xs text-slate-100 w-fit">Use all stored campaign IDs</button>
      <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={campaignTransactionParams.useStoredCampaigns} onChange={(e) => setCampaignTransactionParams((s) => ({ ...s, useStoredCampaigns: e.target.checked }))} className="rounded bg-slate-700 border-slate-600" />Use stored campaign list when Campaign IDs is empty</label>
      <input
        placeholder="Transaction endpoint URL"
        value={credentials.transactionApiUrl}
        onChange={(e) => setCredentials((s) => ({ ...s, transactionApiUrl: e.target.value }))}
        className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100"
      />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <input placeholder="AffiliateId" value={campaignTransactionParams.affiliateId} onChange={(e) => setCampaignTransactionParams((s) => ({ ...s, affiliateId: normalizeMerchantId(e.target.value) }))} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100" />
        <input placeholder="AdId" value={campaignTransactionParams.adId} onChange={(e) => setCampaignTransactionParams((s) => ({ ...s, adId: normalizeMerchantId(e.target.value) }))} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100" />
        <input placeholder="StepId" value={campaignTransactionParams.stepId} onChange={(e) => setCampaignTransactionParams((s) => ({ ...s, stepId: normalizeMerchantId(e.target.value) }))} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100" />
        <input placeholder="PromoCode" value={campaignTransactionParams.promoCode} onChange={(e) => setCampaignTransactionParams((s) => ({ ...s, promoCode: normalizeMerchantId(e.target.value) }))} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100" />
        <select value={campaignTransactionParams.additionalIdType} onChange={(e) => setCampaignTransactionParams((s) => ({ ...s, additionalIdType: e.target.value }))} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100">
          <option value="">AdditionalIdType</option>
          <option value="1">1 - Affiliate Reference ID</option>
          <option value="2">2 - Advertiser Reference ID</option>
          <option value="3">3 - IP Address</option>
          <option value="4">4 - Sub ID’s (Any Matching)</option>
          <option value="5">5 - Sub ID’s (Exact Match)</option>
        </select>
        <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={campaignTransactionParams.isApproved} onChange={(e) => setCampaignTransactionParams((s) => ({ ...s, isApproved: e.target.checked }))} className="rounded bg-slate-700 border-slate-600" />Approved only</label>
      </div>
      <label className="inline-flex items-center gap-2 text-xs text-slate-300">
        <input
          type="checkbox"
          checked={campaignTransactionParams.strictThrottle !== false}
          onChange={(e) => setCampaignTransactionParams((s) => ({ ...s, strictThrottle: e.target.checked }))}
          className="rounded bg-slate-700 border-slate-600"
        />
        Strict throttle mode (recommended) · 1 request per ~2 seconds to avoid LinkTrust 429 limits
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <input
          type="number"
          min="1"
          max="200"
          placeholder="Chunk size (campaigns/request)"
          value={campaignTransactionParams.chunkSize}
          onChange={(e) => setCampaignTransactionParams((s) => ({ ...s, chunkSize: e.target.value.replace(/[^0-9]/g, '') }))}
          className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100"
        />
        <input
          type="number"
          min="0"
          max="30000"
          step="100"
          placeholder="Delay between chunks (ms)"
          value={campaignTransactionParams.interChunkDelayMs}
          onChange={(e) => setCampaignTransactionParams((s) => ({ ...s, interChunkDelayMs: e.target.value.replace(/[^0-9]/g, '') }))}
          className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100"
        />
      </div>
      {campaignTransactionChunkProgress && (
        <p className="text-xs text-blue-300">
          Chunk progress: {campaignTransactionChunkProgress.completedChunks}/{campaignTransactionChunkProgress.totalChunks} chunks · {campaignTransactionChunkProgress.processedCampaigns}/{campaignTransactionChunkProgress.totalCampaigns} campaigns
        </p>
      )}
      <input
        placeholder="AdditionalIds"
        value={campaignTransactionParams.additionalIds}
        onChange={(e) => setCampaignTransactionParams((s) => ({ ...s, additionalIds: e.target.value }))}
        className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100"
      />
      <select
        value={campaignTransactionFormat}
        onChange={(e) => setCampaignTransactionFormat(e.target.value)}
        className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100"
      >
        <option value="raw">Format: Raw</option>
        <option value="raw_and_summary_csv">Format: Raw + Summary CSV (two files)</option>
        <option value="lt7_transaction_details">Format: LT7 Transaction Details (Excel Exact)</option>
        <option value="lt7_transaction_details_excel_raw">Format: LT7 Transaction Details (Excel + Raw)</option>
        <option value="lt7_transaction_details_csv">Format: LT7 Transaction Details (CSV Exact)</option>
        <option value="lt7_transaction_details_csv_raw">Format: LT7 Transaction Details (CSV + Raw)</option>
      </select>
      {['lt7_transaction_details', 'lt7_transaction_details_excel_raw', 'lt7_transaction_details_csv', 'lt7_transaction_details_csv_raw'].includes(campaignTransactionFormat) && (
        <p className="text-[11px] text-slate-500">LT7 format uses <span className="text-slate-300">StringValue</span> for <span className="text-slate-300">Transaction Id</span> when available.</p>
      )}
      <div className="flex flex-wrap gap-2">
        <button onClick={syncCampaignTransactions} disabled={!hasAuthCredentials || syncingCampaignTransactions} className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white rounded-lg">{syncingCampaignTransactions ? 'Running...' : campaignTransactionDownloadLabel}</button>
        <button
          type="button"
          onClick={retryFailedCampaignTransactions}
          disabled={syncingCampaignTransactions || !(campaignTransactionResult?.failedCampaigns || []).length}
          className="px-4 py-2 bg-amber-800 hover:bg-amber-700 disabled:opacity-60 text-amber-100 rounded-lg"
        >
          Retry failed campaigns only
        </button>
      </div>
      <p className="text-xs text-slate-400">{campaignTransactionResult ? `Processed ${campaignTransactionResult.campaignCount || 0} campaign(s), success ${campaignTransactionResult.successfulCampaignCount || 0}, zero-row ${campaignTransactionResult.zeroRowCampaignCount || 0}, received ${campaignTransactionResult.transactionCount || 0} transaction row(s), failed ${campaignTransactionResult.failedCount || 0}.` : 'No transaction run yet.'}</p>
      {campaignTransactionResult && (
        <div className="rounded-md border border-slate-700 bg-slate-900/40 p-2 text-xs text-slate-300 space-y-2">
          <p>Campaign source: <span className="text-slate-100">{campaignTransactionResult.campaignIdSource || 'unknown'}</span></p>
          <p>Status counts: {Object.entries(campaignTransactionResult.failureStatusCounts || {}).map(([code, count]) => `${code}: ${count}`).join(' · ') || 'none'}</p>
          {(campaignTransactionResult.failedCampaigns || []).length > 0 && (
            <div className="max-h-28 overflow-auto rounded border border-rose-700/40 bg-rose-900/10 p-2 space-y-1">
              {(campaignTransactionResult.failedCampaigns || []).slice(0, 20).map((item, idx) => (
                <p key={`campaign-transaction-failure-${idx}`} className="text-rose-200">
                  CID {item.campaignId} · status {item.statusCode || 'n/a'} · attempts {item.attempts || 1} · page {item.pageCount ?? 0} · {item.error || 'Request failed'}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
      </SyncCard>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h2 className="text-3xl font-bold text-slate-100">Automations</h2>
          <p className="text-slate-400 mt-2">Dedicated workspace for LinkTrust data syncing and import automation jobs.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/management" className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg text-sm">Open Management</Link>
          <button onClick={refreshAll} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg inline-flex items-center">
            <RefreshCcw className="w-4 h-4 mr-2" />{refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="admin-glass rounded-xl border border-slate-700/50 p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <input placeholder="LinkTrust username" value={credentials.username} onChange={(e) => setCredentials((s) => ({ ...s, username: e.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
        <input type="password" placeholder="LinkTrust password" value={credentials.password} onChange={(e) => setCredentials((s) => ({ ...s, password: e.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
      </div>

      <div className="admin-glass rounded-xl border border-slate-700/50 p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-slate-100 font-semibold">Client Automation Profiles</h3>
          <p className="text-xs text-slate-500">Encrypted credentials + reusable automation request configs.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input value={automationClientName} onChange={(e) => setAutomationClientName(e.target.value)} placeholder="Client name" className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm" />
          <input value={automationProfileName} onChange={(e) => setAutomationProfileName(e.target.value)} placeholder="Profile name" className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm" />
          <select value={selectedAutomationProfileId} onChange={(e) => setSelectedAutomationProfileId(e.target.value)} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm">
            <option value="">Load saved profile</option>
            {automationProfiles.map((profile) => (
              <option key={`automation-profile-${profile.id}`} value={profile.id}>{profile.client_name} · {profile.profile_name}</option>
            ))}
          </select>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={saveAutomationProfile} disabled={savingAutomationProfile} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg text-sm">{savingAutomationProfile ? 'Saving...' : 'Save'}</button>
            <button type="button" onClick={applyAutomationProfile} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg text-sm">Apply</button>
            <button type="button" onClick={deleteAutomationProfile} className="px-3 py-2 bg-slate-900 hover:bg-slate-800 text-slate-300 rounded-lg text-sm">Delete</button>
          </div>
        </div>
        {appliedAutomationProfile?.profileId && (
          <p className="text-xs text-emerald-300">
            Active profile context: {appliedAutomationProfile.clientName || 'Client'} · {appliedAutomationProfile.profileName || `Profile ${appliedAutomationProfile.profileId}`}
            {appliedAutomationProfile.merchantId ? ` · Merchant ${appliedAutomationProfile.merchantId}` : ''}
            {appliedAutomationProfile.username ? ` · ${appliedAutomationProfile.username}` : ''}
          </p>
        )}
        {getAppliedProfileMismatch() && (
          <p className="text-xs text-amber-300">Profile warning: {getAppliedProfileMismatch()}</p>
        )}
      </div>

      <div className="admin-glass rounded-xl border border-slate-700/50 p-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-2">
          {workflowModes.map((mode) => (
            <button
              key={mode.id}
              type="button"
              onClick={() => setActiveMode(mode.id)}
              className={`text-left rounded-lg border p-3 ${activeMode === mode.id ? 'bg-red-700/20 border-red-600/50' : 'bg-slate-900/40 border-slate-700 hover:border-slate-500'}`}
            >
              <p className="text-sm font-semibold text-slate-100">{mode.label}</p>
              <p className="text-xs text-slate-400 mt-1">{mode.description}</p>
            </button>
          ))}
        </div>
        <div>
          <input list="automation-merchant-options" placeholder="Optional merchant context" value={credentials.merchantId} onChange={(e) => setCredentials((s) => ({ ...s, merchantId: normalizeMerchantId(e.target.value) }))} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
          <datalist id="automation-merchant-options">
            {merchantOptions.map((option) => <option key={`merchant-${option.merchantId}`} value={String(option.merchantId)} label={`${option.merchantName || 'Merchant'} (${option.merchantId})`} />)}
          </datalist>
          <p className="text-xs text-slate-500 mt-1">Used for campaign import and optional workspace filtering only.</p>
          {selectedMerchantOption && <p className="text-xs text-slate-400 mt-1">Context: {selectedMerchantOption.merchantName} ({selectedMerchantOption.merchantId})</p>}
          {!merchantIdIsValid && String(credentials.merchantId || '').trim().length > 0 && <p className="text-xs text-amber-200 mt-1">Merchant ID must be numeric.</p>}
        </div>
      </div>

      <div className="admin-glass rounded-xl border border-slate-700/50 p-2 flex flex-wrap gap-2">
        {visibleWorkflows.map((workflow) => (
          <button
            key={workflow.id}
            onClick={() => setActiveWorkflow(workflow.id)}
            className={`px-3 py-2 text-sm rounded-lg border ${activeWorkflow === workflow.id ? 'bg-red-700/20 border-red-600/50 text-red-200' : 'bg-slate-900/40 border-slate-700 text-slate-300 hover:text-slate-100'}`}
          >
            {workflow.label}
          </button>
        ))}
      </div>

      {error && <div className="rounded-lg border border-red-700/40 bg-red-900/20 p-3 text-red-200 text-sm">{error}</div>}

      {activeWorkflow === 'campaigns' && (
        <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
            <h3 className="text-slate-100 font-semibold inline-flex items-center"><Upload className="w-4 h-4 mr-2 text-red-300" />Campaign Import</h3>
            <p className="text-xs text-slate-500">Preview mapping before submit</p>
          </div>
          <div className="p-4 space-y-3 border-b border-slate-700/50">
            <div className="max-w-xl">
              <input list="automation-merchant-options" placeholder="Merchant ID (required for campaign import)" value={credentials.merchantId} onChange={(e) => setCredentials((s) => ({ ...s, merchantId: normalizeMerchantId(e.target.value) }))} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
              <p className="text-xs text-slate-500 mt-1">Campaign import requires Merchant ID so imported rows can be persisted under the right account.</p>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <label className="inline-flex items-center px-3 py-2 rounded-lg border border-slate-600 bg-slate-800 text-slate-100 cursor-pointer hover:bg-slate-700">
                <input type="file" accept=".csv,.xls,.xlsx" className="hidden" onChange={handleCampaignFile} />Upload campaign file
              </label>
              <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={campaignStopOnError} onChange={(e) => setCampaignStopOnError(e.target.checked)} className="rounded bg-slate-700 border-slate-600" />Stop on first error</label>
              <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={campaignDryRun} onChange={(e) => setCampaignDryRun(e.target.checked)} className="rounded bg-slate-700 border-slate-600" />Dry run</label>
              <button type="button" onClick={() => openMappingDrawer('campaign')} disabled={!campaignRows.length} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-slate-100 rounded-lg text-sm">Open Mapping Drawer</button>
              <button onClick={runCampaignImport} disabled={!campaignRows.length || !hasMerchantCredentials || campaignImporting} className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white rounded-lg">{campaignImporting ? 'Running...' : campaignDryRun ? 'Run Dry Run' : 'Run Import'}</button>
            </div>
            <input
              placeholder="Optional: override category for all rows (must exist in LinkTrust)"
              value={campaignCategoryOverride}
              onChange={(event) => setCampaignCategoryOverride(event.target.value)}
              className="w-full max-w-xl px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm"
            />
            <div className="max-w-xl">
              <select value={mappingAdvertiserScope} onChange={(event) => setMappingAdvertiserScope(event.target.value)} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm">
                <option value="">Default mapping scope (all advertisers)</option>
                {advertiserScopeOptions.map((option) => <option key={option.key} value={option.key}>{option.label} (Merchant {option.merchantId})</option>)}
              </select>
              <p className="text-xs text-slate-500 mt-1">Mapping profiles save per advertiser scope so campaign files can be reused by advertiser.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input placeholder="Mapping profile name" value={campaignProfileName} onChange={(event) => setCampaignProfileName(event.target.value)} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm" />
              <button type="button" onClick={() => saveMappingProfile('campaign')} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg text-sm">Save Profile</button>
              <select value={selectedCampaignProfileId} onChange={(event) => setSelectedCampaignProfileId(event.target.value)} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm">
                <option value="">Load saved campaign profile</option>
                {scopedCampaignProfiles.map((profile) => <option key={`campaign-profile-${profile.id}`} value={profile.id}>{profile.profile_name}</option>)}
              </select>
              <button type="button" onClick={() => applyMappingProfile('campaign')} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg text-sm">Apply</button>
              <button type="button" onClick={() => deleteMappingProfile('campaign')} className="px-3 py-2 bg-slate-900 hover:bg-slate-800 text-slate-300 rounded-lg text-sm">Delete</button>
            </div>
            <p className="text-xs text-slate-400">{campaignFileName || 'No file selected'} {campaignRows.length ? `· ${campaignRows.length} rows · header row ${campaignHeaderRowIndex + 1}` : ''}</p>
          </div>
          <div className="p-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 max-h-80 overflow-auto space-y-2">{campaignPreview.map((row) => <div key={`campaign-preview-${row.index}`} className="text-xs rounded-md border border-slate-700 bg-slate-950/40 p-2"><p className="text-slate-200">Row {row.index + 1}</p><p className="text-slate-400 mt-1">{summarizeMappedRow(row)}</p></div>)}</div>
            <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 max-h-80 overflow-auto space-y-2">
              {!campaignResult ? <p className="text-xs text-slate-500">No run yet.</p> : <><p className="text-xs text-slate-200">Mode: {campaignResult.mode} · Total: {campaignResult.totalRows}</p>{(campaignResult.failed || []).slice(0, 20).map((item) => <div key={`campaign-failed-${item.rowIndex}`} className="text-xs rounded-md border border-rose-700/40 bg-rose-900/10 p-2"><p className="text-rose-200">Row {item.rowIndex + 1}</p><p className="text-rose-300/80">{(item.errors || []).join(' | ')}</p></div>)}</>}
            </div>
          </div>
        </div>
      )}

      {activeWorkflow === 'creatives' && (
        <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
            <h3 className="text-slate-100 font-semibold inline-flex items-center"><Wand2 className="w-4 h-4 mr-2 text-red-300" />Creative Import</h3>
            <p className="text-xs text-slate-500">Links auto-tokenized to `[=TrackingURL=]`</p>
          </div>
          <div className="p-4 space-y-3 border-b border-slate-700/50">
            <div className="flex flex-wrap gap-3 items-center">
              <label className="inline-flex items-center px-3 py-2 rounded-lg border border-slate-600 bg-slate-800 text-slate-100 cursor-pointer hover:bg-slate-700"><input type="file" accept=".csv,.xls,.xlsx" className="hidden" onChange={handleCreativeFile} />Upload creative file</label>
              <label className="inline-flex items-center px-3 py-2 rounded-lg border border-slate-600 bg-slate-800 text-slate-100 cursor-pointer hover:bg-slate-700"><input type="file" multiple className="hidden" onChange={handleAssetUpload} />{uploadingAssets ? 'Uploading assets...' : 'Upload files/images'}</label>
              <label className="inline-flex items-center px-3 py-2 rounded-lg border border-slate-600 bg-slate-800 text-slate-100 cursor-pointer hover:bg-slate-700"><input type="file" multiple className="hidden" onChange={handleAssetUpload} webkitdirectory="true" directory="true" />{uploadingAssets ? 'Uploading folder...' : 'Upload folder'}</label>
              <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={creativeDryRun} onChange={(e) => setCreativeDryRun(e.target.checked)} className="rounded bg-slate-700 border-slate-600" />Dry run</label>
              <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={creativeLocalOnly} onChange={(e) => setCreativeLocalOnly(e.target.checked)} className="rounded bg-slate-700 border-slate-600" />Import local only (skip LinkTrust)</label>
              <button type="button" onClick={() => openMappingDrawer('creative')} disabled={!creativeRows.length} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-slate-100 rounded-lg text-sm">Open Mapping Drawer</button>
              <button onClick={runCreativeImport} disabled={!creativeRows.length || (!creativeLocalOnly && !hasAuthCredentials) || creativeImporting} className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white rounded-lg">{creativeImporting ? 'Running...' : creativeLocalOnly ? 'Run Local Import' : creativeDryRun ? 'Run Dry Run' : 'Run Import'}</button>
            </div>
            <div className="max-w-xl">
              <select value={creativeCampaignOverride} onChange={(event) => setCreativeCampaignOverride(event.target.value)} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm">
                <option value="">Auto-assign campaign per row/name/id</option>
                {campaignOptions.map((campaign) => (
                  <option key={`creative-campaign-${campaign.linktrust_campaign_id}`} value={String(campaign.linktrust_campaign_id)}>
                    {campaign.campaign_name} ({campaign.linktrust_campaign_id})
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">Tip: for asset-only uploads, run Campaign Directory Sync first or select a campaign override to avoid unresolved campaign IDs.</p>
            </div>
            <div className="max-w-xl">
              <select value={mappingAdvertiserScope} onChange={(event) => setMappingAdvertiserScope(event.target.value)} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm">
                <option value="">Default mapping scope (all advertisers)</option>
                {advertiserScopeOptions.map((option) => <option key={`creative-${option.key}`} value={option.key}>{option.label} (Merchant {option.merchantId})</option>)}
              </select>
              <p className="text-xs text-slate-500 mt-1">Creative mapping profiles can be saved per advertiser scope.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input placeholder="Mapping profile name" value={creativeProfileName} onChange={(event) => setCreativeProfileName(event.target.value)} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm" />
              <button type="button" onClick={() => saveMappingProfile('creative')} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg text-sm">Save Profile</button>
              <select value={selectedCreativeProfileId} onChange={(event) => setSelectedCreativeProfileId(event.target.value)} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm">
                <option value="">Load saved creative profile</option>
                {scopedCreativeProfiles.map((profile) => <option key={`creative-profile-${profile.id}`} value={profile.id}>{profile.profile_name}</option>)}
              </select>
              <button type="button" onClick={() => applyMappingProfile('creative')} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg text-sm">Apply</button>
              <button type="button" onClick={() => deleteMappingProfile('creative')} className="px-3 py-2 bg-slate-900 hover:bg-slate-800 text-slate-300 rounded-lg text-sm">Delete</button>
            </div>
            <p className="text-xs text-slate-400">{creativeFileName || 'No file selected'} {creativeRows.length ? `· ${creativeRows.length} rows · header row ${creativeHeaderRowIndex + 1}` : ''}</p>

            {scannedCreativeRows.length > 0 && (
              <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm text-slate-100 font-medium">Zip/Folder Creative Scanner</p>
                  <button type="button" onClick={applyScannedRowsToCreativeImport} className="px-3 py-1.5 bg-red-700 hover:bg-red-600 text-white rounded text-xs">Apply Scanner Rows To Import</button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Review detected campaign/ad mappings from nested folders before import.</p>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2">
                  <label className="text-[11px] text-slate-400">Campaign ID Source
                    <select value={scannerMapper.campaignId} onChange={(event) => setScannerMapper((state) => ({ ...state, campaignId: event.target.value }))} className="mt-1 w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-slate-100 text-xs">
                      <option value="detectedCampaignId">Detected Campaign ID</option>
                      <option value="detectedAdId">Detected ADID</option>
                    </select>
                  </label>
                  <label className="text-[11px] text-slate-400">Campaign Name Source
                    <select value={scannerMapper.campaignName} onChange={(event) => setScannerMapper((state) => ({ ...state, campaignName: event.target.value }))} className="mt-1 w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-slate-100 text-xs">
                      <option value="detectedCampaignName">Detected Campaign Name</option>
                      <option value="fallbackCampaignName">Fallback Folder Name</option>
                    </select>
                  </label>
                  <label className="text-[11px] text-slate-400">ADID Source
                    <select value={scannerMapper.adId} onChange={(event) => setScannerMapper((state) => ({ ...state, adId: event.target.value }))} className="mt-1 w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-slate-100 text-xs">
                      <option value="detectedAdId">Detected ADID</option>
                      <option value="detectedCampaignId">Detected Campaign ID</option>
                    </select>
                  </label>
                  <label className="text-[11px] text-slate-400">Creative Name Source
                    <select value={scannerMapper.creativeName} onChange={(event) => setScannerMapper((state) => ({ ...state, creativeName: event.target.value }))} className="mt-1 w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-slate-100 text-xs">
                      <option value="detectedCreativeName">Detected Ad Name</option>
                      <option value="fallbackCreativeName">File/Folder Name</option>
                    </select>
                  </label>
                </div>
                <div className="mt-3 max-h-56 overflow-auto rounded border border-slate-700 bg-slate-950/40">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-900/90 text-slate-400">
                      <tr>
                        <th className="text-left px-2 py-2">Campaign ID</th>
                        <th className="text-left px-2 py-2">Campaign Name</th>
                        <th className="text-left px-2 py-2">ADID</th>
                        <th className="text-left px-2 py-2">Creative Name</th>
                        <th className="text-left px-2 py-2">File</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scannedCreativeRows.slice(0, 500).map((row, rowIndex) => (
                        <tr key={`scan-row-${rowIndex}`} className="border-t border-slate-700/50 text-slate-200">
                          <td className="px-2 py-1"><input value={row.campaignId || ''} onChange={(event) => updateScannedCreativeRow(rowIndex, 'campaignId', event.target.value.replace(/[^0-9]/g, ''))} className="w-24 px-2 py-1 bg-slate-800 border border-slate-600 rounded" /></td>
                          <td className="px-2 py-1"><input value={row.campaignName || ''} onChange={(event) => updateScannedCreativeRow(rowIndex, 'campaignName', event.target.value)} className="w-40 px-2 py-1 bg-slate-800 border border-slate-600 rounded" /></td>
                          <td className="px-2 py-1"><input value={row.adId || ''} onChange={(event) => updateScannedCreativeRow(rowIndex, 'adId', event.target.value.replace(/[^0-9]/g, ''))} className="w-24 px-2 py-1 bg-slate-800 border border-slate-600 rounded" /></td>
                          <td className="px-2 py-1"><input value={row.name || ''} onChange={(event) => updateScannedCreativeRow(rowIndex, 'name', event.target.value)} className="w-40 px-2 py-1 bg-slate-800 border border-slate-600 rounded" /></td>
                          <td className="px-2 py-1 text-slate-500 break-all">{row.fileName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <div className="p-4 grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 max-h-80 overflow-auto space-y-2">{creativePreview.map((row) => <div key={`creative-preview-${row.index}`} className="text-xs rounded-md border border-slate-700 bg-slate-950/40 p-2"><p className="text-slate-200">Row {row.index + 1}</p><p className="text-slate-400 mt-1">{summarizeCreativeMappedRow(row)}</p></div>)}</div>
            <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 max-h-80 overflow-auto space-y-2">{uploadedAssets.length === 0 ? <p className="text-xs text-slate-500">No assets uploaded yet.</p> : uploadedAssets.map((asset) => <div key={asset.storedName} className="text-xs rounded-md border border-slate-700 bg-slate-950/40 p-2"><p className="text-slate-200 break-all">{asset.originalName}</p><p className="text-slate-500 break-all">{asset.url}</p></div>)}</div>
            <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 max-h-80 overflow-auto space-y-2">{!creativeResult ? <p className="text-xs text-slate-500">No run yet.</p> : <><p className="text-xs text-slate-200">Mode: {creativeResult.mode} · Total: {creativeResult.totalRows}</p>{(creativeResult.failed || []).slice(0, 20).map((item) => <div key={`creative-failed-${item.rowIndex}`} className="text-xs rounded-md border border-rose-700/40 bg-rose-900/10 p-2"><p className="text-rose-200">Row {item.rowIndex + 1}</p><p className="text-rose-300/80">{(item.errors || []).join(' | ')}</p></div>)}</>}</div>
          </div>
          <div className="p-4 border-t border-slate-700/50">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-slate-100">Campaign Creative Manager</h4>
              <p className="text-xs text-slate-500">Imported and synced creatives are persisted and reusable in Bulk Links.</p>
            </div>
            <div className="mt-3 max-h-56 overflow-auto rounded-lg border border-slate-700 bg-slate-900/40">
              {campaignOptions.length === 0 ? (
                <p className="p-3 text-xs text-slate-500">No campaigns available yet. Run campaign sync/import first.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-slate-900/70 text-slate-400 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Campaign</th>
                      <th className="text-left px-3 py-2">Stored creatives</th>
                      <th className="text-left px-3 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaignOptions.slice(0, 250).map((campaign) => {
                      const campaignId = Number(campaign.linktrust_campaign_id);
                      const creativeCount = (creativeOptionsByCampaign[String(campaignId)] || []).length;
                      return (
                        <tr key={`creative-manager-${campaignId}`} className="border-t border-slate-700/60 text-slate-200">
                          <td className="px-3 py-2">{campaign.campaign_name || `Campaign ${campaignId}`} ({campaignId})</td>
                          <td className="px-3 py-2">{creativeCount}</td>
                          <td className="px-3 py-2">
                            <button type="button" onClick={() => setCreativeCampaignOverride(String(campaignId))} className="px-2 py-1 bg-slate-800 hover:bg-slate-700 rounded text-slate-100">Assign imports here</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {activeWorkflow === 'sync' && (
        <div className="space-y-4">
          <div className="admin-glass rounded-xl border border-slate-700/50 p-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSyncSectionTab('directory')}
              className={syncTabButtonClass('directory')}
            >
              Directory Sync
            </button>
            <button
              type="button"
              onClick={() => setSyncSectionTab('exports')}
              className={syncTabButtonClass('exports')}
            >
              Export Jobs
            </button>
          </div>

          {syncSectionTab === 'directory' && directorySyncContent}
          {syncSectionTab === 'exports' && exportJobsContent}

          <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
            <div className="p-3 border-b border-slate-700/50 flex items-center justify-between">
              <h4 className="text-slate-100 text-sm font-semibold">Automation Activity Log</h4>
              <button
                type="button"
                onClick={() => setAutomationActivityLog([])}
                className="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded text-xs"
              >
                Clear
              </button>
            </div>
            <div className="max-h-56 overflow-auto p-3 space-y-1">
              {automationActivityLog.length === 0 ? (
                <p className="text-xs text-slate-500">No activity yet. Run sync/export actions to populate logs.</p>
              ) : (
                automationActivityLog.slice().reverse().map((entry) => (
                  <p
                    key={entry.id}
                    className={`text-xs ${entry.level === 'error' ? 'text-rose-300' : entry.level === 'warn' ? 'text-amber-300' : entry.level === 'success' ? 'text-emerald-300' : 'text-slate-300'}`}
                  >
                    [{entry.ts ? new Date(entry.ts).toLocaleTimeString() : 'n/a'}] {String(entry.area || 'general').toUpperCase()} · {entry.message}{entry.details ? ` — ${entry.details}` : ''}
                  </p>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {activeWorkflow === 'links' && (
        <div className="space-y-4">
          <div className="admin-glass rounded-xl border border-slate-700/50 p-4">
            <h3 className="text-slate-100 font-semibold inline-flex items-center"><Link2 className="w-4 h-4 mr-2 text-red-300" />Bulk Link Builder</h3>
            <p className="text-xs text-slate-500 mt-1">Build base links and campaign-scoped creative links (`ADID`) in one run.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} className="md:col-span-2 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
              <div className="flex flex-col gap-2">
                <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={includeBaseLinks} onChange={(e) => setIncludeBaseLinks(e.target.checked)} className="rounded bg-slate-700 border-slate-600" />Include base links</label>
                <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={includeAdIds} onChange={(e) => setIncludeAdIds(e.target.checked)} className="rounded bg-slate-700 border-slate-600" />Include creative ADID links</label>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-2 mt-3">
              <input value={bulkCampaignSearch} onChange={(event) => setBulkCampaignSearch(event.target.value)} placeholder="Search campaigns" className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm" />
              <select value={bulkAdvertiserFilter} onChange={(event) => setBulkAdvertiserFilter(event.target.value)} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm">
                <option value="">All advertisers</option>
                {bulkAdvertiserOptions.map((option) => <option key={`bulk-advertiser-${option.merchantId}`} value={String(option.merchantId)}>{option.label}</option>)}
              </select>
              <select value={bulkCategoryFilter} onChange={(event) => setBulkCategoryFilter(event.target.value)} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm">
                <option value="">All categories</option>
                {bulkCategoryOptions.map((category) => <option key={`bulk-category-${category}`} value={category}>{category}</option>)}
              </select>
              <div className="flex gap-2">
                <button type="button" onClick={selectFilteredCampaigns} disabled={!filteredCampaignOptions.length} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-slate-100 rounded-lg text-sm">Select filtered</button>
                <button type="button" onClick={clearFilteredCampaigns} disabled={!filteredCampaignOptions.length} className="px-3 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-slate-300 rounded-lg text-sm">Clear filtered</button>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">Filtered campaigns: {filteredCampaignOptions.length} of {campaignOptions.length}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <button onClick={syncCreativesForSelectedCampaigns} disabled={syncingCreatives || !selectedCampaignIds.length || !hasAuthCredentials} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-slate-100 rounded-lg text-sm">{syncingCreatives ? 'Syncing creatives...' : 'Pull all assets for selected campaigns'}</button>
              <button onClick={loadAdIdsFromSelectedCampaigns} disabled={!selectedCampaignIds.length} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-slate-100 rounded-lg text-sm">Load ADIDs from selected campaigns</button>
            </div>
          </div>

          <div className="admin-glass rounded-xl border border-slate-700/50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h4 className="text-slate-100 text-sm font-semibold">Campaign Asset Coverage</h4>
              <p className="text-xs text-slate-400">
                Total: {filteredCampaignAssetCoverage.total} · Ready: {filteredCampaignAssetCoverage.complete} · Needs ADIDs: {filteredCampaignAssetCoverage.missing}
              </p>
            </div>
            <div className="max-h-48 overflow-auto rounded-lg border border-slate-700 bg-slate-900/40">
              {filteredCampaignAssetCoverage.rows.length === 0 ? (
                <p className="p-3 text-xs text-slate-500">No campaigns loaded yet.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-slate-900/60 text-slate-400 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Campaign</th>
                      <th className="text-left px-3 py-2">Synced ADIDs</th>
                      <th className="text-left px-3 py-2">Selected ADIDs</th>
                      <th className="text-left px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCampaignAssetCoverage.rows.slice(0, 250).map((row) => (
                      <tr key={`coverage-row-${row.campaignId}`} className="border-t border-slate-700/60 text-slate-200">
                        <td className="px-3 py-2">{row.campaignName} ({row.campaignId})</td>
                        <td className="px-3 py-2">{row.syncedCount}</td>
                        <td className="px-3 py-2">{row.selectedCount}</td>
                        <td className="px-3 py-2">
                          {row.needsManual ? (
                            <span className="text-amber-300">Needs ADIDs</span>
                          ) : row.hasManualOnly ? (
                            <span className="text-blue-300">Manual ADIDs added</span>
                          ) : row.allSyncedSelected ? (
                            <span className="text-emerald-300">Ready</span>
                          ) : (
                            <span className="text-slate-300">Partially selected</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="admin-glass rounded-xl border border-slate-700/50 p-3 max-h-96 overflow-auto">
              <p className="text-xs text-slate-400 mb-2">Campaigns ({filteredCampaignOptions.length})</p>
              {filteredCampaignOptions.map((campaign) => (
                <div key={`campaign-select-${campaign.id}`} className="rounded-md border border-slate-700 bg-slate-900/40 p-2 mb-2">
                  <label className="flex items-start gap-2 text-xs text-slate-300">
                    <input type="checkbox" checked={selectedCampaignIds.includes(campaign.linktrust_campaign_id)} onChange={() => toggleCampaignSelection(campaign.linktrust_campaign_id)} className="mt-0.5 rounded bg-slate-700 border-slate-600" />
                    <span>
                      {campaign.campaign_name} ({campaign.linktrust_campaign_id || 'n/a'})
                      <span className="block text-[11px] text-slate-500 mt-0.5">
                        {(advertiserLabelByMerchant[Number(campaign.merchant_id)] || `Merchant ${campaign.merchant_id || 'n/a'}`)} · {campaign.category || 'Uncategorized'}
                      </span>
                    </span>
                  </label>
                  {selectedCampaignIds.includes(campaign.linktrust_campaign_id) && (
                    <div className="mt-2 pl-5 space-y-2">
                      <div className="grid grid-cols-1 gap-2">
                        <div className="flex gap-2">
                          <input
                            value={campaignAdDrafts[String(campaign.linktrust_campaign_id)] || ''}
                            onChange={(event) => setCampaignAdDrafts((state) => ({ ...state, [String(campaign.linktrust_campaign_id)]: event.target.value }))}
                            placeholder="Add ADIDs (comma separated)"
                            className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100"
                          />
                          <button type="button" onClick={() => addManualCampaignAdIds(campaign.linktrust_campaign_id)} className="px-2 py-1.5 bg-slate-800 hover:bg-slate-700 rounded text-xs text-slate-100">Add</button>
                        </div>
                      </div>
                      <div className="max-h-28 overflow-auto space-y-1">
                        {((creativeOptionsByCampaign[String(campaign.linktrust_campaign_id)] || []).length === 0 && (campaignAdSelections[String(campaign.linktrust_campaign_id)] || []).length === 0) ? (
                          <p className="text-[11px] text-slate-500">No synced ADIDs yet. Use "Pull all assets" or add manually.</p>
                        ) : (
                          Array.from(new Set([
                            ...(creativeOptionsByCampaign[String(campaign.linktrust_campaign_id)] || []).map((item) => item.adId),
                            ...((campaignAdSelections[String(campaign.linktrust_campaign_id)] || []).map((value) => Number(value)))
                          ])).sort((a, b) => a - b).map((adId) => (
                            <label key={`campaign-${campaign.linktrust_campaign_id}-adid-${adId}`} className="flex items-center gap-2 text-[11px] text-slate-300">
                              <input
                                type="checkbox"
                                checked={(campaignAdSelections[String(campaign.linktrust_campaign_id)] || []).includes(adId)}
                                onChange={() => toggleCampaignAdId(campaign.linktrust_campaign_id, adId)}
                                className="rounded bg-slate-700 border-slate-600"
                              />
                              <span>ADID {adId}</span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="admin-glass rounded-xl border border-slate-700/50 p-3 max-h-96 overflow-auto">
              <p className="text-xs text-slate-400 mb-2">Affiliates ({storedAffiliates.length})</p>
              {storedAffiliates.map((affiliate) => (
                <label key={`affiliate-select-${affiliate.affiliate_id}`} className="flex items-start gap-2 text-xs text-slate-300 mb-2">
                  <input type="checkbox" checked={selectedAffiliateIds.includes(affiliate.affiliate_id)} onChange={() => toggleSelection(affiliate.affiliate_id, setSelectedAffiliateIds)} className="mt-0.5 rounded bg-slate-700 border-slate-600" />
                  <span>{affiliate.affiliate_name || affiliate.affiliate_id} ({affiliate.affiliate_id})</span>
                </label>
              ))}
            </div>
            <div className="admin-glass rounded-xl border border-slate-700/50 p-3 max-h-96 overflow-auto">
              <p className="text-xs text-slate-400 mb-2">Selected Campaign ADIDs ({selectedAdIds.length})</p>
              {!selectedCampaignIds.length ? (
                <p className="text-xs text-slate-500">Select campaign(s) to manage associated ADIDs.</p>
              ) : selectedCampaignIds.map((campaignId) => {
                const selectedForCampaign = campaignAdSelections[String(campaignId)] || [];
                return (
                  <div key={`campaign-adid-summary-${campaignId}`} className="mb-3 rounded-md border border-slate-700 bg-slate-900/40 p-2">
                    <p className="text-[11px] text-slate-400 mb-1">Campaign {campaignId}</p>
                    {!selectedForCampaign.length ? (
                      <p className="text-[11px] text-slate-500">No ADIDs selected.</p>
                    ) : (
                      <p className="text-[11px] text-slate-300 break-words">{selectedForCampaign.join(', ')}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex gap-3">
            <button onClick={() => generateLinks(true)} disabled={generatingLinks} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg">Preview Links</button>
            <button onClick={() => generateLinks(false)} disabled={generatingLinks} className="px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg">Generate + Save Links</button>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="admin-glass rounded-xl border border-slate-700/50 p-3 max-h-80 overflow-auto">
              <p className="text-xs text-slate-400 mb-2">Generated Preview</p>
              {linksResult?.links?.length ? linksResult.links.slice(0, 200).map((link, index) => <p key={`preview-link-${index}`} className="text-xs text-slate-300 break-all mb-1">{link.url}</p>) : <p className="text-xs text-slate-500">No preview generated yet.</p>}
            </div>
            <div className="admin-glass rounded-xl border border-slate-700/50 p-3 max-h-80 overflow-auto">
              <p className="text-xs text-slate-400 mb-2">Saved Links ({storedLinks.length})</p>
              {storedLinks.length ? storedLinks.slice(0, 200).map((link) => <p key={`stored-link-${link.id}`} className="text-xs text-slate-300 break-all mb-1">{link.generated_url}</p>) : <p className="text-xs text-slate-500">No persisted links yet.</p>}
            </div>
          </div>
        </div>
      )}

      <div className="text-xs text-slate-500">Campaign management, creative operations, and bulk links now live in the Management workspace.</div>

      {mappingDrawer.open && (
        <div className="fixed inset-0 z-40">
          <button type="button" onClick={() => setMappingDrawer((state) => ({ ...state, open: false }))} className="absolute inset-0 bg-slate-950/70" />
          <aside className="absolute right-0 top-0 h-full w-full max-w-2xl bg-slate-900 border-l border-slate-700 shadow-2xl p-4 overflow-auto">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-slate-100 font-semibold">{mappingDrawer.type === 'creative' ? 'Creative' : 'Campaign'} Mapping Drawer</h3>
                <p className="text-xs text-slate-400 mt-1">{activeFileName || 'No file selected'} · {activeRawRows.length} rows · header row {activeHeaderRow + 1}</p>
              </div>
              <button type="button" onClick={() => setMappingDrawer((state) => ({ ...state, open: false }))} className="p-2 rounded-md border border-slate-700 text-slate-300 hover:text-slate-100">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="text-xs text-slate-400">
                Inspect row
                <input type="number" min="1" max={Math.max(activeRawRows.length, 1)} value={activeRawRows.length ? mappingRowIndex + 1 : 1} onChange={(e) => {
                  const next = Math.max(1, Math.min(Number(e.target.value || 1), activeRawRows.length || 1));
                  setMappingRowIndex(next - 1);
                }} className="mt-1 w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-slate-100" />
              </label>
            </div>

            <div className="mt-4 rounded-lg border border-slate-700 bg-slate-950/40 p-3">
              <p className="text-xs text-slate-400 mb-2">Header Mapping Overrides (applied on import submit)</p>
              <div className="max-h-48 overflow-auto space-y-2">
                {activeHeaders.length === 0 ? <p className="text-xs text-slate-500">No headers detected.</p> : activeHeaders.map((header) => (
                  <div key={`override-${mappingDrawer.type}-${header}`} className="grid grid-cols-1 md:grid-cols-2 gap-2 items-center">
                    <p className="text-xs text-slate-200 break-all">{header}</p>
                    <select value={activeFieldOverrides[header] || ''} onChange={(event) => updateActiveFieldOverride(header, event.target.value)} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100">
                      {activeFieldOptions.map((option) => <option key={`${mappingDrawer.type}-${header}-${option.value || 'auto'}`} value={option.value}>{option.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4">
              <div className="rounded-lg border border-slate-700 bg-slate-950/40 p-3">
                <p className="text-xs text-slate-400 mb-2">Mapped Fields</p>
                {!activeMappedRow || !Object.keys(activeMappedRow.mapped || {}).length ? <p className="text-xs text-slate-500">No recognized mapped fields.</p> : Object.entries(activeMappedRow.mapped || {}).map(([key, value]) => (
                  <p key={`mapped-${key}`} className="text-xs text-slate-200 mb-1"><span className="text-slate-400">{key}:</span> {String(value)}</p>
                ))}
              </div>

              <div className="rounded-lg border border-slate-700 bg-slate-950/40 p-3">
                <p className="text-xs text-slate-400 mb-2">Unmapped Fields (will be fallback/ignored)</p>
                {!activeMappedRow || !(activeMappedRow.leftovers || []).length ? <p className="text-xs text-slate-500">No unmapped fields.</p> : (activeMappedRow.leftovers || []).map((item, index) => (
                  <p key={`leftover-${index}`} className="text-xs text-slate-200 mb-1"><span className="text-slate-400">{item.key}:</span> {String(item.value)}</p>
                ))}
              </div>

              <div className="rounded-lg border border-slate-700 bg-slate-950/40 p-3">
                <p className="text-xs text-slate-400 mb-2">Raw Source Row</p>
                <pre className="text-xs text-slate-200 overflow-auto">{JSON.stringify(activeSourceRow || {}, null, 2)}</pre>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default Automations;
